import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api, { getHealingRequests, addSession, returnPatient, getSessions, getMe, requestSessionFeedback, recallSessionFeedback, getNotifications, markNotificationsRead, raiseHealerInvoice, getPractitionerActivities, practitionerActivityCheckin, earlyComplete } from '../utils/api';
import ActivityPanel from '../components/ActivityPanel';
import ChakraScanForm from '../components/ChakraScanForm';
import { useWindowSize } from '../utils/useWindowSize';
 
const HealerDashboard = () => {
  const { user, logout } = useAuth();
  const { isMobile } = useWindowSize();
  const [requests, setRequests] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [canViewSessions, setCanViewSessions] = useState(false);
  const [enrolling, setEnrolling] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('home');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showSessionForm, setShowSessionForm] = useState(false);
  const [expandedPatient, setExpandedPatient] = useState(null);
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [returnRequest, setReturnRequest] = useState(null);
  const [returnReasonCode, setReturnReasonCode] = useState('');
  const [returnRemarks, setReturnRemarks] = useState('');
  const [sessionForm, setSessionForm] = useState({
    date: new Date().toISOString().split('T')[0],
    duration: 60,
    session_notes_internal: '',
    session_notes_patient: '',
    status: 'completed'
  });
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifPanel, setShowNotifPanel] = useState(false);
  const [invoiceModal, setInvoiceModal] = useState(null); // requestId or null
  const [invoiceForm, setInvoiceForm] = useState({ sessionsClaimed: 1, notes: '' });
  const [activitiesOpen, setActivitiesOpen] = useState(null); // requestId
  const [activitiesMap, setActivitiesMap] = useState({}); // { requestId: [activities] }
  const [myPractActivities, setMyPractActivities] = useState([]);
  const [showScanForm, setShowScanForm] = useState(false);
  const [scanResults, setScanResults] = useState([]);
  const [expandedScanSessions, setExpandedScanSessions] = useState({});
  const [raisingInvoice, setRaisingInvoice] = useState(false);
  const [showFinalSessionWarning, setShowFinalSessionWarning] = useState(false);
  const [earlyCompleteModal, setEarlyCompleteModal] = useState(null);
  const [earlyCompleteReason, setEarlyCompleteReason] = useState('');
  const [submittingEarlyComplete, setSubmittingEarlyComplete] = useState(false);

  useEffect(function() {
    fetchRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(function() {
    var fetchNotifs = async function() {
      try {
        var res = await getNotifications();
        setNotifications(res.data.notifications || []);
        setUnreadCount(res.data.unreadCount || 0);
      } catch (err) {}
    };
    fetchNotifs();
    var interval = setInterval(fetchNotifs, 60000);
    return function() { clearInterval(interval); };
  }, []);

  const fetchRequests = async () => {
    try {
      var results = await Promise.all([getHealingRequests(), getSessions(), getMe(), getPractitionerActivities()]);
      setRequests(results[0].data.requests);
      setSessions(results[1].data.sessions || []);
      if (results[2].data.user && results[2].data.user.allowSessionView) {
        setCanViewSessions(true);
      }
      setMyPractActivities(results[3].data || []);
    } catch (error) {
      console.log('Error:', error);
    } finally {
      setLoading(false);
    }
  };
 
  const handleAddSession = async (e) => {
    e.preventDefault();
    // Check if this is the final session
    var planned = selectedRequest.sessions_planned;
    var done = selectedRequest.totalSessionsCompleted || 0;
    var isFinalSession = planned && (done + 1 >= planned);
    if (isFinalSession && !showFinalSessionWarning) {
      setShowFinalSessionWarning(true);
      return;
    }
    setShowFinalSessionWarning(false);
    try {
      await addSession(selectedRequest._id, Object.assign({}, sessionForm, { scanningResults: scanResults }));
      alert('Session remarks saved successfully.');
      setShowSessionForm(false);
      setSelectedRequest(null);
      setSessionForm({ date: new Date().toISOString().split('T')[0], duration: 60, session_notes_internal: '', session_notes_patient: '', status: 'completed' });
      setShowScanForm(false);
      setScanResults([]);
      fetchRequests();
    } catch (error) {
      alert(error.response ? error.response.data.message : 'Error saving session');
    }
  };

  var invoiceGeneratedCheck = function() {
    if (!selectedRequest) return true;
    return selectedRequest.payment && (selectedRequest.payment.billNumber || selectedRequest.payment.billGeneratedAt);
  };
 
  const handleReturnPatient = async () => {
    if (!returnReasonCode) {
      alert('Please select a reason.');
      return;
    }
    if (!returnRemarks) {
      alert('Please enter remarks.');
      return;
    }
    try {
      await returnPatient(returnRequest._id, {
        reason_code: returnReasonCode,
        healer_return_remarks: returnRemarks
      });
      alert('Patient returned to admin successfully.');
      setShowReturnModal(false);
      setReturnRequest(null);
      setReturnReasonCode('');
      setReturnRemarks('');
      fetchRequests();
    } catch (error) {
      alert(error.response ? error.response.data.message : 'Error returning patient');
    }
  };

  const handleRaiseInvoice = async function(requestId) {
    setRaisingInvoice(true);
    try {
      await raiseHealerInvoice(requestId, invoiceForm);
      setInvoiceModal(null);
      setInvoiceForm({ sessionsClaimed: 1, notes: '' });
      fetchRequests();
      alert('Invoice raised successfully. Admin has been notified.');
    } catch (error) {
      var d = error.response && error.response.data;
      alert(d && d.message ? d.message : (d && d.error ? d.error : error.message || 'Error raising invoice'));
    } finally {
      setRaisingInvoice(false);
    }
  };

  const handleEnroll = async function(sessionId, sessionTitle) {
    if (!window.confirm('Enroll in "' + sessionTitle + '"?')) return;
    setEnrolling(sessionId);
    try {
      await api.post('/sessions/' + sessionId + '/enroll');
      alert('Enrolled successfully!');
      fetchRequests();
    } catch (error) {
      alert(error.response ? error.response.data.message : 'Error enrolling');
    } finally {
      setEnrolling(null);
    }
  };

  var handleRequestSessionFeedback = async function(reqId, sessionIndex, sessionDate, patientName) {
    if (!window.confirm('Request feedback from ' + patientName + ' for session on ' + sessionDate + '? An email and notification will be sent.')) return;
    try {
      var res = await requestSessionFeedback(reqId, sessionIndex);
      if (res.data.emailSent) {
        alert('Feedback request sent. Email + notification delivered.');
      } else {
        alert('Feedback requested (notification sent). Email failed: ' + (res.data.emailError || 'unknown'));
      }
      fetchRequests();
    } catch (error) {
      alert(error.response ? error.response.data.message : 'Error requesting feedback');
    }
  };

  var handleRecallSessionFeedback = async function(reqId, sessionIndex, sessionDate) {
    if (!window.confirm('Recall the pending feedback request for session on ' + sessionDate + '? The patient will no longer be prompted.')) return;
    try {
      await recallSessionFeedback(reqId, sessionIndex);
      fetchRequests();
    } catch (error) {
      alert(error.response ? error.response.data.message : 'Error recalling feedback');
    }
  };

  var handleEarlyComplete = async function() {
    if (!earlyCompleteReason.trim()) { alert('Please enter a reason for early closure.'); return; }
    setSubmittingEarlyComplete(true);
    try {
      await earlyComplete(earlyCompleteModal._id, { reason: earlyCompleteReason });
      alert('Case marked as healed successfully. Completion email sent to patient.');
      setEarlyCompleteModal(null);
      setEarlyCompleteReason('');
      fetchRequests();
    } catch (error) {
      alert(error.response ? error.response.data.message : 'Error closing case');
    } finally {
      setSubmittingEarlyComplete(false);
    }
  };

  const active = requests.filter(function(r) { return r.status === 'assigned' || r.status === 'in_progress'; });
  const completed = requests.filter(function(r) { return r.status === 'completed'; });
  const totalSessions = requests.reduce(function(sum, r) { return sum + (r.totalSessionsCompleted || 0); }, 0);

  const printHealerStatement = function(req) {
    var healerName = user ? user.firstName + ' ' + (user.lastName || '') : 'Healer';
    var healerEmail = user ? user.email : '';
    var patientName = req.patient ? req.patient.firstName + ' ' + req.patient.lastName : (req.manualPatientName || 'Patient');
    var payments = req.healerPayments || [];
    var totalSettled = payments.filter(function(p) { return p.status === 'settled'; }).reduce(function(s, p) { return s + (p.amountClaimed || 0); }, 0);
    var totalEntitlement = (req.payment && req.payment.healerShareAmount) || 0;
    var pending = totalEntitlement - totalSettled;
    var w = window.open('', '_blank');
    w.document.write('<!DOCTYPE html><html><head><meta charset="utf-8"><title>Payment Statement - ' + healerName + '</title><style>body{font-family:Arial,sans-serif;margin:0;padding:0;color:#333}@media print{.no-print{display:none}}.page{max-width:680px;margin:0 auto;padding:40px}.header{background:linear-gradient(135deg,#071e12,#0c2617);padding:28px 36px;display:flex;justify-content:space-between;align-items:center}.logo{height:44px}.accent{height:3px;background:linear-gradient(to right,#00AEEF,#4a8f82,#8DC63F)}.body{padding:36px}.divider{border:none;border-top:1px solid #e0e0e0;margin:20px 0}.table{width:100%;border-collapse:collapse;margin:20px 0}.table th{background:#f9faf7;padding:10px 14px;text-align:left;font-size:11px;text-transform:uppercase;letter-spacing:1px;color:#888;border-bottom:2px solid #e0e0e0}.table td{padding:12px 14px;border-bottom:1px solid #f0f0f0;font-size:13px}.settled{color:#27ae60;font-weight:700}.pending-s{color:#d4ac0d;font-weight:700}.total-row{background:#071e12;color:#fff}.total-row td{padding:14px;font-weight:700;font-size:14px}.footer{background:#f9faf7;border-top:1px solid #e0e0e0;padding:20px 36px;font-size:11px;color:#888;text-align:center}.print-btn{background:#4a8f82;color:#fff;border:none;padding:12px 28px;border-radius:6px;font-size:14px;font-weight:700;cursor:pointer;margin-bottom:20px}</style></head><body>');
    w.document.write('<div class="page">');
    w.document.write('<button class="no-print print-btn" onclick="window.print()">Print / Save as PDF</button>');
    w.document.write('<div class="header"><img class="logo" src="https://pranicwellnesscentre.com/wp-content/uploads/2018/10/PH-Revised-Logo.png" alt="PWC"><div style="text-align:right;color:rgba(255,255,255,0.7);font-size:12px"><div style="color:#8DC63F;font-weight:700;font-size:14px;margin-bottom:4px">PAYMENT STATEMENT</div>Pranic Wellness Centre</div></div>');
    w.document.write('<div class="accent"></div>');
    w.document.write('<div class="body">');
    w.document.write('<div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:24px"><div><div style="font-size:11px;text-transform:uppercase;letter-spacing:1px;color:#888;margin-bottom:6px">Healer</div><div style="font-weight:700;font-size:18px;color:#1a1a2e">' + healerName + '</div>' + (healerEmail ? '<div style="font-size:12px;color:#666">' + healerEmail + '</div>' : '') + '</div><div style="text-align:right"><div style="font-size:11px;text-transform:uppercase;letter-spacing:1px;color:#888;margin-bottom:6px">Case Details</div><div style="font-weight:700;color:#1a1a2e">' + req.condition + '</div><div style="font-size:12px;color:#666">Patient: ' + patientName + '</div><div style="font-size:12px;color:#666">Sessions done: ' + (req.totalSessionsCompleted || 0) + '</div><div style="font-size:12px;color:#666">Statement date: ' + new Date().toLocaleDateString('en-IN') + '</div></div></div>');
    w.document.write('<hr class="divider">');
    w.document.write('<div style="display:grid;grid-template-columns:repeat(3,1fr);gap:16px;margin-bottom:24px;background:#f9faf7;padding:16px;border-radius:8px">');
    w.document.write('<div style="text-align:center"><div style="font-size:22px;font-weight:700;color:#4a8f82">' + (req.totalSessionsCompleted || 0) + '</div><div style="font-size:11px;color:#888;text-transform:uppercase;letter-spacing:1px;margin-top:4px">Sessions Done</div></div>');
    w.document.write('<div style="text-align:center"><div style="font-size:22px;font-weight:700;color:#1a1a2e">₹' + totalEntitlement.toLocaleString('en-IN') + '</div><div style="font-size:11px;color:#888;text-transform:uppercase;letter-spacing:1px;margin-top:4px">Total Entitlement</div></div>');
    w.document.write('<div style="text-align:center"><div style="font-size:22px;font-weight:700;color:#27ae60">₹' + totalSettled.toLocaleString('en-IN') + '</div><div style="font-size:11px;color:#888;text-transform:uppercase;letter-spacing:1px;margin-top:4px">Total Settled</div></div>');
    w.document.write('</div>');
    if (payments.length > 0) {
      w.document.write('<table class="table"><thead><tr><th>Invoice #</th><th>Sessions</th><th>Amount Claimed</th><th>Raised On</th><th>Status</th><th>Settled On</th></tr></thead><tbody>');
      payments.forEach(function(hp) {
        var cls = hp.status === 'settled' ? 'settled' : 'pending-s';
        w.document.write('<tr><td><strong>' + (hp.paymentNumber || '—') + '</strong></td><td>' + (hp.sessionsClaimed || 0) + '</td><td style="font-weight:700">₹' + (hp.amountClaimed || 0).toLocaleString('en-IN') + '</td><td>' + new Date(hp.raisedAt).toLocaleDateString('en-IN') + '</td><td class="' + cls + '">' + hp.status.toUpperCase() + '</td><td>' + (hp.settledAt ? new Date(hp.settledAt).toLocaleDateString('en-IN') : '—') + '</td></tr>');
      });
      w.document.write('</tbody><tfoot><tr class="total-row"><td colspan="2" style="color:rgba(255,255,255,0.7)">Settled</td><td style="color:#8DC63F">₹' + totalSettled.toLocaleString('en-IN') + '</td><td colspan="2" style="color:rgba(255,255,255,0.7)">Balance Pending</td><td style="color:' + (pending > 0 ? '#f0c040' : '#8DC63F') + '">₹' + pending.toLocaleString('en-IN') + '</td></tr></tfoot></table>');
    } else {
      w.document.write('<div style="background:#f9faf7;padding:20px;text-align:center;color:#999;border-radius:8px">No payment records yet.</div>');
    }
    w.document.write('</div>');
    w.document.write('<div class="footer">Pranic Wellness Centre | Kurinji Nagar, Perungudi, Chennai – 600096 | +91 91768 80807<br>This is a computer-generated statement and does not require a signature.</div>');
    w.document.write('</div></body></html>');
    w.document.close();
  };
 
  const todayStr = new Date().toLocaleDateString('en-IN');
 
  const urgencyColors = {
    normal: { bg: '#eafaf1', color: '#27ae60' },
    urgent: { bg: '#fef9e7', color: '#d4ac0d' },
    critical: { bg: '#fdf0f0', color: '#e74c3c' }
  };
 
  const statusColors = {
    assigned: { bg: '#eaf4fb', color: '#2980b9' },
    in_progress: { bg: '#eafaf1', color: '#27ae60' },
    completed: { bg: '#f0f0f0', color: '#666' }
  };

  var s = {
    ...styles,
    topbarInner: isMobile ? { ...styles.topbarInner, padding: '12px 16px', height: 'auto', flexWrap: 'wrap', gap: '8px' } : styles.topbarInner,
    container: isMobile ? { ...styles.container, padding: '0 16px' } : styles.container,
    tabs: isMobile ? { ...styles.tabs, overflowX: 'auto', WebkitOverflowScrolling: 'touch', width: '100%' } : styles.tabs,
    welcomeBanner: isMobile ? { ...styles.welcomeBanner, flexDirection: 'column', padding: '20px 16px', alignItems: 'flex-start' } : styles.welcomeBanner,
    welcomeStats: isMobile ? { ...styles.welcomeStats, flexWrap: 'wrap', gap: '12px', width: '100%', padding: '12px 16px' } : styles.welcomeStats,
    welcomeStatDivider: isMobile ? { ...styles.welcomeStatDivider, display: 'none' } : styles.welcomeStatDivider,
    homeGrid: isMobile ? { ...styles.homeGrid, gridTemplateColumns: '1fr' } : styles.homeGrid,
    modal: isMobile ? { ...styles.modal, alignItems: 'flex-end', padding: '0' } : styles.modal,
    modalCard: isMobile ? { ...styles.modalCard, borderRadius: '16px 16px 0 0', maxWidth: '100%', maxHeight: '90vh', overflowY: 'auto' } : styles.modalCard,
    formGrid: isMobile ? { ...styles.formGrid, gridTemplateColumns: '1fr' } : styles.formGrid,
    modalBtns: isMobile ? { ...styles.modalBtns, flexDirection: 'column' } : styles.modalBtns,
    submitBtn: isMobile ? { ...styles.submitBtn, width: '100%' } : styles.submitBtn,
    cancelBtn: isMobile ? { ...styles.cancelBtn, width: '100%' } : styles.cancelBtn,
    patientCardHeader: isMobile ? { ...styles.patientCardHeader, flexDirection: 'column', gap: '12px' } : styles.patientCardHeader,
    patientCardActions: isMobile ? { ...styles.patientCardActions, flexWrap: 'wrap' } : styles.patientCardActions,
    patientItem: isMobile ? { ...styles.patientItem, gridTemplateColumns: '40px 1fr' } : styles.patientItem,
    patientActions: isMobile ? { ...styles.patientActions, flexDirection: 'row', flexWrap: 'wrap' } : styles.patientActions,
  };

  return (
    <div style={s.layout}>

      {/* Top Bar */}
      <div style={s.topbar}>
        <div style={s.topbarInner}>
          <img src="https://pranicwellnesscentre.com/wp-content/uploads/2018/10/PH-Revised-Logo.png" alt="Pranic Wellness Centre" style={s.logo} />
          <div style={s.topbarRight}>
            <div style={s.userInfo}>
              <span style={s.userName}>Welcome, {user && user.firstName}</span>
              <span style={s.userRole}>Certified Healer</span>
            </div>
            <div style={{ position: 'relative' }}>
              <button onClick={function() {
                setShowNotifPanel(!showNotifPanel);
                if (!showNotifPanel && unreadCount > 0) {
                  markNotificationsRead().then(function() { setUnreadCount(0); setNotifications(function(prev) { return prev.map(function(n) { return Object.assign({}, n, { read: true }); }); }); }).catch(function() {});
                }
              }} style={{ background: 'none', border: 'none', cursor: 'pointer', position: 'relative', padding: '4px', color: 'rgba(255,255,255,0.6)', fontSize: '18px', display: 'flex', alignItems: 'center' }}>
                <span style={{ fontSize: '18px' }}>🔔</span>
                {unreadCount > 0 && (
                  <span style={{ position: 'absolute', top: '-2px', right: '-4px', background: '#e74c3c', color: '#fff', borderRadius: '10px', padding: '1px 5px', fontSize: '9px', fontWeight: '700', minWidth: '14px', textAlign: 'center', lineHeight: '14px' }}>{unreadCount}</span>
                )}
              </button>
              {showNotifPanel && (
                <div style={{ position: 'absolute', top: '40px', right: 0, width: '300px', maxHeight: '360px', overflowY: 'auto', background: '#fff', borderRadius: '8px', boxShadow: '0 8px 24px rgba(0,0,0,0.15)', zIndex: 1000, border: '1px solid #e0e0e0' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', borderBottom: '1px solid #e0e0e0' }}>
                    <span style={{ fontFamily: "'Raleway', sans-serif", fontSize: '11px', fontWeight: '700', letterSpacing: '1.5px', textTransform: 'uppercase', color: '#666' }}>Notifications</span>
                    <button onClick={function() { setShowNotifPanel(false); }} style={{ background: 'none', border: 'none', color: '#999', cursor: 'pointer', fontSize: '16px' }}>✕</button>
                  </div>
                  <div>
                    {notifications.length === 0 ? (
                      <div style={{ padding: '20px', textAlign: 'center', color: '#999', fontSize: '12px' }}>No notifications</div>
                    ) : notifications.slice(0, 20).map(function(n, i) {
                      return (
                        <div key={i} style={{ padding: '10px 14px', borderBottom: '1px solid #f0f0f0', background: n.read ? '#fff' : '#f9faf7' }}>
                          <div style={{ fontSize: '12px', color: '#333', lineHeight: '1.5' }}>{n.message}</div>
                          <div style={{ fontSize: '10px', color: '#999', marginTop: '3px' }}>{new Date(n.createdAt).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            <button onClick={logout} style={s.logoutBtn}>Logout</button>
          </div>
        </div>
      </div>
 
      {/* Mark as Healed (Early Close) Modal */}
      {earlyCompleteModal && (
        <div style={s.modal}>
          <div style={s.modalCard}>
            <div style={s.modalTitle}>Mark Case as Healed</div>
            <div style={s.modalPatientInfo}>
              <div style={s.modalPatientName}>{earlyCompleteModal.patient ? (earlyCompleteModal.patient.firstName + ' ' + earlyCompleteModal.patient.lastName) : (earlyCompleteModal.manualPatientName || '')}</div>
              <div style={s.modalPatientDetail}>
                Condition: {earlyCompleteModal.condition} &nbsp;|&nbsp; Sessions done: {earlyCompleteModal.totalSessionsCompleted} of {earlyCompleteModal.sessions_planned || '?'} planned
              </div>
            </div>
            <div style={{ background: '#eafaf1', border: '1px solid #a9dfbf', borderRadius: '6px', padding: '12px 14px', marginBottom: '16px', fontSize: '12px', color: '#1a6b5a', lineHeight: '1.6' }}>
              Closing early will set sessions planned to <strong>{earlyCompleteModal.totalSessionsCompleted}</strong> (matching actual). All active patient activities will be ended and a completion email will be sent.
            </div>
            <div style={s.field}>
              <label style={s.label}>Reason for Early Closure *</label>
              <textarea style={{ ...s.input, height: '80px', resize: 'vertical' }}
                placeholder="e.g. Patient recovered ahead of schedule..."
                value={earlyCompleteReason}
                onChange={function(e) { setEarlyCompleteReason(e.target.value); }} />
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button onClick={function() { setEarlyCompleteModal(null); setEarlyCompleteReason(''); }} style={s.cancelBtn} disabled={submittingEarlyComplete}>Cancel</button>
              <button onClick={handleEarlyComplete} disabled={submittingEarlyComplete}
                style={{ background: '#27ae60', color: '#fff', border: 'none', borderRadius: '4px', padding: '12px 24px', fontFamily: "'Raleway', sans-serif", fontSize: '12px', fontWeight: '700', cursor: submittingEarlyComplete ? 'not-allowed' : 'pointer', opacity: submittingEarlyComplete ? 0.7 : 1 }}>
                {submittingEarlyComplete ? 'Closing...' : 'Confirm — Mark as Healed'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Final Session Warning Modal */}
      {showFinalSessionWarning && selectedRequest && (
        <div style={s.modal}>
          <div style={{ ...s.modalCard, maxWidth: '460px' }}>
            <div style={{ fontSize: '18px', fontWeight: '700', fontFamily: "'Raleway', sans-serif", color: '#1a1a2e', marginBottom: '8px' }}>
              Final Session — Case Closing
            </div>
            <div style={{ fontSize: '13px', color: '#555', marginBottom: '20px', lineHeight: '1.6' }}>
              You are about to complete the <strong>last planned session</strong> for <strong>{selectedRequest.patient ? selectedRequest.patient.firstName + ' ' + selectedRequest.patient.lastName : 'this patient'}</strong> ({selectedRequest.condition}).
            </div>
            <div style={{ background: invoiceGeneratedCheck() ? '#eafaf1' : '#fff8f0', border: '1px solid ' + (invoiceGeneratedCheck() ? '#27ae60' : '#e67e22'), borderRadius: '8px', padding: '14px 16px', marginBottom: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontSize: '20px' }}>{invoiceGeneratedCheck() ? '✓' : '⚠'}</span>
                <div>
                  <div style={{ fontSize: '13px', fontWeight: '700', color: invoiceGeneratedCheck() ? '#27ae60' : '#e67e22' }}>
                    {invoiceGeneratedCheck() ? 'Patient invoice has been generated' : 'Patient invoice not yet generated'}
                  </div>
                  {!invoiceGeneratedCheck() && (
                    <div style={{ fontSize: '12px', color: '#888', marginTop: '2px' }}>Please ensure the admin generates the invoice before or after this session.</div>
                  )}
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button type="button" onClick={function() { setShowFinalSessionWarning(false); }}
                style={{ padding: '9px 20px', borderRadius: '6px', border: '1px solid #ddd', background: '#fff', color: '#555', fontSize: '13px', fontWeight: '700', cursor: 'pointer' }}>
                Go Back
              </button>
              <button type="button" onClick={function() { setShowFinalSessionWarning(false); handleAddSession({ preventDefault: function() {} }); }}
                style={{ padding: '9px 20px', borderRadius: '6px', border: 'none', background: '#4a8f82', color: '#fff', fontSize: '13px', fontWeight: '700', cursor: 'pointer' }}>
                Save & Close Case
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Session Modal */}
      {showSessionForm && selectedRequest && (
        <div style={s.modal}>
          <div style={{ ...s.modalCard, maxWidth: '740px', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={s.modalTitle}>Add Session Remarks</div>
            <div style={s.modalPatientInfo}>
              <div style={s.modalPatientName}>{selectedRequest.patient ? (selectedRequest.patient.firstName + ' ' + selectedRequest.patient.lastName) : (selectedRequest.manualPatientName || '')}</div>
              <div style={s.modalPatientDetail}>Condition: {selectedRequest.condition} | Sessions done: {selectedRequest.totalSessionsCompleted}</div>
            </div>
            <form onSubmit={handleAddSession}>
              <div style={s.formGrid}>
                <div style={s.field}>
                  <label style={s.label}>Session Date</label>
                  <input style={s.input} type="date" value={sessionForm.date}
                    onChange={function(e) { setSessionForm({ ...sessionForm, date: e.target.value }); }} required />
                </div>
                <div style={s.field}>
                  <label style={s.label}>Duration (minutes)</label>
                  <input style={s.input} type="number" value={sessionForm.duration}
                    onChange={function(e) { setSessionForm({ ...sessionForm, duration: e.target.value }); }} required />
                </div>
                <div style={s.field}>
                  <label style={s.label}>Session Status</label>
                  <select style={s.input} value={sessionForm.status}
                    onChange={function(e) { setSessionForm({ ...sessionForm, status: e.target.value }); }}>
                    <option value="completed">Completed</option>
                    <option value="scheduled">Scheduled</option>
                    <option value="cancelled">Cancelled</option>
                    <option value="no_show">No Show</option>
                  </select>
                </div>
                <div style={{ ...s.field, gridColumn: '1 / -1' }}>
                  <div style={{ background: '#fef9e7', border: '1px solid #d4ac0d', borderLeft: '4px solid #d4ac0d', borderRadius: '6px', padding: '16px', marginBottom: '16px' }}>
                    <label style={{ fontFamily: "'Raleway', sans-serif", fontSize: '11px', fontWeight: '700', letterSpacing: '1.5px', textTransform: 'uppercase', color: '#7D6608', marginBottom: '4px', display: 'block' }}>Internal Remarks</label>
                    <div style={{ fontSize: '11px', color: '#7D6608', marginBottom: '8px', fontStyle: 'italic' }}>(Not visible to patient)</div>
                    <textarea style={{ ...s.input, height: '90px', resize: 'vertical', background: '#fff' }}
                      value={sessionForm.session_notes_internal}
                      onChange={function(e) { setSessionForm({ ...sessionForm, session_notes_internal: e.target.value }); }}
                      placeholder="Clinical notes, energy work details, internal observations..." />
                  </div>
                  <div style={{ background: '#eafaf1', border: '1px solid #27ae60', borderLeft: '4px solid #27ae60', borderRadius: '6px', padding: '16px' }}>
                    <label style={{ fontFamily: "'Raleway', sans-serif", fontSize: '11px', fontWeight: '700', letterSpacing: '1.5px', textTransform: 'uppercase', color: '#1a5c38', marginBottom: '4px', display: 'block' }}>Session Notes for Patient</label>
                    <div style={{ fontSize: '11px', color: '#1a5c38', marginBottom: '8px', fontStyle: 'italic' }}>(Visible to patient in their healing history)</div>
                    <textarea style={{ ...s.input, height: '90px', resize: 'vertical', background: '#fff' }}
                      value={sessionForm.session_notes_patient}
                      onChange={function(e) { setSessionForm({ ...sessionForm, session_notes_patient: e.target.value }); }}
                      placeholder="Progress updates, guidance, recommendations for the patient..." />
                  </div>
                </div>
              </div>
              {/* Chakra Scanning Toggle */}
              <div style={{ marginTop: '8px', marginBottom: '16px' }}>
                <button type="button"
                  onClick={function() { setShowScanForm(function(v) { return !v; }); setScanResults([]); }}
                  style={{ display: 'flex', alignItems: 'center', gap: '10px', width: '100%', padding: '14px 18px', background: showScanForm ? '#1a6b5a' : '#f0f9f7', border: '1px solid #1a6b5a', borderRadius: '8px', cursor: 'pointer', fontFamily: "'Raleway', sans-serif" }}>
                  <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: showScanForm ? '#fff' : '#1a6b5a', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <span style={{ fontSize: '16px' }}>&#9889;</span>
                  </div>
                  <div style={{ textAlign: 'left' }}>
                    <div style={{ fontSize: '13px', fontWeight: '700', color: showScanForm ? '#fff' : '#1a6b5a', letterSpacing: '0.5px' }}>Chakra Scanning Results</div>
                    <div style={{ fontSize: '11px', color: showScanForm ? 'rgba(255,255,255,0.7)' : '#888', marginTop: '2px' }}>{showScanForm ? 'Recording before & after energy/activation levels' : 'Optional — Record scanning data for this session'}</div>
                  </div>
                  <div style={{ marginLeft: 'auto', fontSize: '18px', color: showScanForm ? '#fff' : '#1a6b5a' }}>{showScanForm ? '▲' : '▼'}</div>
                </button>
                {showScanForm && (
                  <div style={{ border: '1px solid #e0e0e0', borderTop: 'none', borderRadius: '0 0 8px 8px', padding: '16px', overflowX: 'auto' }}>
                    <ChakraScanForm onChange={setScanResults} />
                    {scanResults.length > 0 && (
                      <div style={{ marginTop: '10px', padding: '8px 12px', background: '#eafaf1', borderRadius: '6px', fontSize: '12px', color: '#27ae60', fontWeight: '600' }}>
                        ✓ {scanResults.length} chakra reading{scanResults.length > 1 ? 's' : ''} recorded
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div style={s.modalBtns}>
                <button type="submit" style={s.submitBtn}>Save Remarks</button>
                <button type="button" onClick={function() { setShowSessionForm(false); setSelectedRequest(null); setShowScanForm(false); setScanResults([]); }} style={s.cancelBtn}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Return to Admin Modal */}
      {showReturnModal && returnRequest && (
        <div style={s.modal}>
          <div style={s.modalCard}>
            <div style={s.modalTitle}>Return Patient to Admin</div>
            <div style={s.modalPatientInfo}>
              <div style={s.modalPatientName}>{returnRequest.patient ? (returnRequest.patient.firstName + ' ' + returnRequest.patient.lastName) : (returnRequest.manualPatientName || '')}</div>
              <div style={s.modalPatientDetail}>Condition: {returnRequest.condition} | Sessions done: {returnRequest.totalSessionsCompleted}</div>
            </div>
            <div style={s.field}>
              <label style={s.label}>Reason Code *</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '6px' }}>
                {[
                  { value: 'no_show', label: 'No Show' },
                  { value: 'cancelled', label: 'Cancelled' },
                  { value: 'unresponsive', label: 'Unresponsive' },
                  { value: 'other', label: 'Other' }
                ].map(function(opt) {
                  return (
                    <label key={opt.value} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#333', cursor: 'pointer' }}>
                      <input type="radio" name="returnReason" value={opt.value}
                        checked={returnReasonCode === opt.value}
                        onChange={function() { setReturnReasonCode(opt.value); }} />
                      <span style={{
                        ...s.badge,
                        background: opt.value === 'no_show' ? '#fdf0f0' : opt.value === 'cancelled' ? '#fef9e7' : opt.value === 'unresponsive' ? '#fdf0f0' : '#f0f0f0',
                        color: opt.value === 'no_show' ? '#e74c3c' : opt.value === 'cancelled' ? '#d4ac0d' : opt.value === 'unresponsive' ? '#922B21' : '#666'
                      }}>{opt.label}</span>
                    </label>
                  );
                })}
              </div>
            </div>
            <div style={s.field}>
              <label style={s.label}>Internal Remarks (not visible to patient) *</label>
              <textarea style={{ ...s.input, height: '100px', resize: 'vertical', background: '#fef9e7', borderLeft: '4px solid #d4ac0d' }}
                value={returnRemarks}
                onChange={function(e) { setReturnRemarks(e.target.value); }}
                placeholder="Explain why you are returning this patient to admin..." required />
            </div>
            <div style={s.modalBtns}>
              <button onClick={handleReturnPatient} style={{ ...s.submitBtn, background: '#e74c3c' }}>Confirm Return</button>
              <button onClick={function() { setShowReturnModal(false); setReturnRequest(null); setReturnReasonCode(''); setReturnRemarks(''); }} style={s.cancelBtn}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      <div style={s.main}>
        <div style={s.container}>

          {/* Tabs */}
          <div style={s.tabs}>
            {[
              { key: 'home', label: 'Dashboard' },
              { key: 'active', label: 'Active Patients (' + active.length + ')' },
              { key: 'completed', label: 'Completed (' + completed.length + ')' }
            ].concat(canViewSessions ? [{ key: 'sessions', label: 'Sessions (' + sessions.filter(function(s) { return s.status === 'upcoming'; }).length + ')' }] : [])
            .concat([{ key: 'performance', label: 'My Performance' }, { key: 'my-activities', label: 'My Activities' + (myPractActivities.filter(function(a) { return a.isActive; }).length > 0 ? ' (' + myPractActivities.filter(function(a) { return a.isActive; }).length + ')' : '') }])
            .map(function(tab) {
              return (
                <button key={tab.key} onClick={function() { setActiveTab(tab.key); }}
                  style={activeTab === tab.key ? { ...s.tab, ...s.tabActive } : s.tab}>
                  {tab.label}
                </button>
              );
            })}
          </div>
 
          {loading ? (
            <div style={s.loading}>Loading your dashboard...</div>
          ) : (
            <div>
 
              {/* HOME TAB */}
              {activeTab === 'home' && (
                <div>
                  {/* Welcome Banner */}
                  <div style={s.welcomeBanner}>
                    <div style={s.welcomeLeft}>
                      <div style={s.welcomeGreeting}>Hello, {user && user.firstName}!</div>
                      <div style={s.welcomeText}>
                        Welcome to your Healer Dashboard — manage your assigned patients, record session remarks and track healing progress.
                      </div>
                      <div style={s.welcomeDate}>Today: {todayStr}</div>
                    </div>
                    <div style={s.welcomeStats}>
                      <div style={s.welcomeStat}>
                        <div style={s.welcomeStatNum}>{requests.length}</div>
                        <div style={s.welcomeStatLabel}>Total Patients</div>
                      </div>
                      <div style={s.welcomeStatDivider}></div>
                      <div style={s.welcomeStat}>
                        <div style={{ ...s.welcomeStatNum, color: '#27ae60' }}>{active.length}</div>
                        <div style={s.welcomeStatLabel}>Active</div>
                      </div>
                      <div style={s.welcomeStatDivider}></div>
                      <div style={s.welcomeStat}>
                        <div style={{ ...s.welcomeStatNum, color: '#2980b9' }}>{totalSessions}</div>
                        <div style={s.welcomeStatLabel}>Sessions Done</div>
                      </div>
                      <div style={s.welcomeStatDivider}></div>
                      <div style={s.welcomeStat}>
                        <div style={{ ...s.welcomeStatNum, color: '#666' }}>{completed.length}</div>
                        <div style={s.welcomeStatLabel}>Completed</div>
                      </div>
                    </div>
                  </div>
 
                  <div style={s.homeGrid}>
 
                    {/* Active Patients List */}
                    <div style={{ ...s.card, gridColumn: isMobile ? '1 / -1' : '1 / 3' }}>
                      <div style={s.cardHeader}>
                        <div>
                          <div style={s.cardTitle}>Active Patients</div>
                          <div style={s.cardSub}>Patients currently assigned to you</div>
                        </div>
                        <button onClick={function() { setActiveTab('active'); }} style={s.cardLink}>View All</button>
                      </div>
                      <div style={s.cardBody}>
                        {active.length === 0 ? (
                          <div style={s.empty}>
                            <div style={s.emptyTitle}>No active patients</div>
                            <div style={s.emptySub}>Admin will assign patients to you. Check back soon.</div>
                          </div>
                        ) : active.map(function(req) {
                          return (
                            <div key={req._id} style={s.patientItem}>
                              <div style={s.patientAvatar}>
                                {req.patient ? req.patient.firstName.charAt(0) : (req.manualPatientName ? req.manualPatientName.charAt(0) : '')}
                                {req.patient ? req.patient.lastName.charAt(0) : (req.manualPatientName ? (req.manualPatientName.split(' ')[1] || '').charAt(0) : '')}
                              </div>
                              <div style={s.patientInfo}>
                                <div style={s.patientName}>{req.patient ? (req.patient.firstName + ' ' + req.patient.lastName) : (req.manualPatientName || '')}</div>
                                {req.patient && req.patient.showHealerNameToPatient && req.patient.phone && (
                                  <div style={s.patientContact}>{req.patient.phone} | {req.patient.email}</div>
                                )}
                                <div style={s.patientCondition}>{req.condition}</div>
                              </div>
                              <div style={s.patientMid}>
                                <div style={s.sessionCount}>{req.totalSessionsCompleted}{req.sessions_planned ? '/' + req.sessions_planned : ''}</div>
                                <div style={s.sessionCountLabel}>Sessions{req.sessions_remaining != null ? ' (' + req.sessions_remaining + ' left)' : ''}</div>
                              </div>
                              <div style={s.patientActions}>
                                <span style={{ ...s.badge, background: urgencyColors[req.urgency] ? urgencyColors[req.urgency].bg : '#f0f0f0', color: urgencyColors[req.urgency] ? urgencyColors[req.urgency].color : '#666' }}>{req.urgency}</span>
                                {req.patient && req.patient.showHealerNameToPatient && req.patient.phone && (
                                  <a href={'https://wa.me/' + req.patient.phone.replace(/[^0-9]/g, '') + '?text=' + encodeURIComponent('Hello ' + req.patient.firstName + ', this is your healer from Pranic Wellness Centre.')}
                                    target="_blank" rel="noreferrer"
                                    style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '28px', height: '28px', background: '#25D366', color: '#fff', borderRadius: '50%', textDecoration: 'none', fontSize: '14px' }}
                                    title={'WhatsApp ' + req.patient.firstName}>
                                    WA
                                  </a>
                                )}
                                <button onClick={function() { setSelectedRequest(req); setShowSessionForm(true); }} style={s.addSessionBtn}>
                                  + Add Session
                                </button>
                                <button onClick={function() { setReturnRequest(req); setShowReturnModal(true); }} style={s.returnBtn}>
                                  Return to Admin
                                </button>
                                <button onClick={function() { setEarlyCompleteModal(req); setEarlyCompleteReason(''); }}
                                  style={{ ...s.returnBtn, borderColor: '#27ae60', color: '#27ae60' }}>
                                  Mark as Healed
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
 
                    {/* Quick Actions */}
                    <div style={s.card}>
                      <div style={s.cardHeader}>
                        <div style={s.cardTitle}>Quick Actions</div>
                      </div>
                      <div style={{ padding: '16px' }}>
                        {[
                          { label: 'Add Session Remarks', desc: 'Record healing session details for a patient', action: function() { if (active.length > 0) { setSelectedRequest(active[0]); setShowSessionForm(true); } else { alert('No active patients assigned.'); } }, color: '#4a8f82' },
                          { label: 'View Active Patients', desc: 'See all patients currently assigned to you', action: function() { setActiveTab('active'); }, color: '#5a9a6e' },
                          { label: 'View Completed Cases', desc: 'Review your completed healing cases', action: function() { setActiveTab('completed'); }, color: '#2980b9' },
                          { label: 'Contact Centre', desc: 'Get in touch with admin', action: function() { window.open('https://wa.me/919176880807', '_blank'); }, color: '#25D366' }
                        ].map(function(action) {
                          return (
                            <button key={action.label} onClick={action.action} style={s.quickAction}>
                              <div style={{ ...s.quickActionDot, background: action.color }}></div>
                              <div>
                                <div style={s.quickActionLabel}>{action.label}</div>
                                <div style={s.quickActionDesc}>{action.desc}</div>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
 
                  </div>
                </div>
              )}
 
              {/* ACTIVE PATIENTS TAB */}
              {activeTab === 'active' && (
                <div style={s.patientList}>
                  {active.length === 0 ? (
                    <div style={s.emptyCard}>
                      <div style={s.emptyTitle}>No active patients assigned</div>
                      <div style={s.emptySub}>Admin will assign patients to you. Check back soon.</div>
                    </div>
                  ) : active.map(function(req) {
                    return (
                      <div key={req._id} style={s.patientCard}>
                        <div style={s.patientCardHeader}>
                          <div style={s.patientCardLeft}>
                            <div style={s.patientAvatarLg}>
                              {req.patient ? req.patient.firstName.charAt(0) : (req.manualPatientName ? req.manualPatientName.charAt(0) : '')}
                              {req.patient ? req.patient.lastName.charAt(0) : (req.manualPatientName ? (req.manualPatientName.split(' ')[1] || '').charAt(0) : '')}
                            </div>
                            <div>
                              <div style={s.patientCardName}>{req.patient ? (req.patient.firstName + ' ' + req.patient.lastName) : (req.manualPatientName || '')}</div>
                              {req.patient && req.patient.showHealerNameToPatient && req.patient.phone ? (
                                <div style={s.patientCardContact}>{req.patient.email} | {req.patient.phone}</div>
                              ) : (
                                <div style={{ fontSize: '11px', color: '#ccc', fontStyle: 'italic' }}>Contact info hidden by admin</div>
                              )}
                              <div style={s.patientCardCondition}>{req.condition}</div>
                            </div>
                          </div>
                          <div style={s.patientCardRight}>
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>
                              {req.patient && req.patient.showHealerNameToPatient && req.patient.phone && (
                                <a href={'https://wa.me/' + req.patient.phone.replace(/[^0-9]/g, '') + '?text=' + encodeURIComponent('Hello ' + req.patient.firstName + ', this is your healer from Pranic Wellness Centre.')}
                                  target="_blank" rel="noreferrer"
                                  style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '32px', height: '32px', background: '#25D366', color: '#fff', borderRadius: '50%', textDecoration: 'none', fontSize: '13px', fontWeight: '700' }}
                                  title={'WhatsApp ' + req.patient.firstName}>
                                  WA
                                </a>
                              )}
                              <div>
                                <div style={s.patientCardStatNum}>{req.totalSessionsCompleted}{req.sessions_planned ? '/' + req.sessions_planned : ''}</div>
                                <div style={s.patientCardStatLabel}>Sessions{req.sessions_remaining != null ? ' (' + req.sessions_remaining + ' left)' : ''}</div>
                              </div>
                            </div>
                            <div style={s.patientCardBadges}>
                              <span style={{ ...s.badge, background: urgencyColors[req.urgency] ? urgencyColors[req.urgency].bg : '#f0f0f0', color: urgencyColors[req.urgency] ? urgencyColors[req.urgency].color : '#666' }}>{req.urgency}</span>
                              <span style={{ ...s.badge, background: statusColors[req.status] ? statusColors[req.status].bg : '#f0f0f0', color: statusColors[req.status] ? statusColors[req.status].color : '#666' }}>{req.status ? req.status.replace('_', ' ') : ''}</span>
                            </div>
                          </div>
                        </div>

                        <div style={s.patientCardDesc}>{req.description}</div>

                        {/* Session Planning Summary */}
                        {req.sessions_planned && (
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', background: '#f9faf7', borderRadius: '6px', padding: '14px', marginBottom: '16px' }}>
                            <div style={{ textAlign: 'center' }}>
                              <div style={{ fontFamily: "'Raleway', sans-serif", fontSize: '20px', fontWeight: '700', color: '#4a8f82' }}>{req.sessions_planned}</div>
                              <div style={{ fontSize: '10px', color: '#999', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: '700' }}>Planned</div>
                            </div>
                            <div style={{ textAlign: 'center' }}>
                              <div style={{ fontFamily: "'Raleway', sans-serif", fontSize: '20px', fontWeight: '700', color: '#4a8f82' }}>{req.totalSessionsCompleted || 0}</div>
                              <div style={{ fontSize: '10px', color: '#999', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: '700' }}>Done</div>
                            </div>
                            <div style={{ textAlign: 'center' }}>
                              <div style={{ fontFamily: "'Raleway', sans-serif", fontSize: '20px', fontWeight: '700', color: (req.sessions_remaining || 0) > 0 ? '#27ae60' : '#e74c3c' }}>
                                {req.sessions_remaining != null ? req.sessions_remaining : (req.sessions_planned - (req.totalSessionsCompleted || 0))}
                              </div>
                              <div style={{ fontSize: '10px', color: '#999', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: '700' }}>Remaining</div>
                            </div>
                          </div>
                        )}

                        <div style={s.patientCardActions}>
                          <button onClick={function() { setSelectedRequest(req); setShowSessionForm(true); }}
                            style={req.status === 'completed' ? { ...s.addSessionBtn, opacity: 0.5, cursor: 'not-allowed' } : s.addSessionBtn}
                            disabled={req.status === 'completed'}>
                            + Add Session Remarks
                          </button>
                          <button onClick={function() { setReturnRequest(req); setShowReturnModal(true); }} style={s.returnBtn}>
                            Return to Admin
                          </button>
                          <button onClick={function() { setEarlyCompleteModal(req); setEarlyCompleteReason(''); }}
                            style={{ ...s.returnBtn, borderColor: '#27ae60', color: '#27ae60' }}>
                            Mark as Healed
                          </button>
                          <button onClick={function() { setExpandedPatient(expandedPatient === req._id ? null : req._id); }} style={s.expandBtn}>
                            {expandedPatient === req._id ? 'Hide History' : 'View Session History'}
                          </button>
                          <button onClick={function() {
                            setActivitiesOpen(activitiesOpen === req._id ? null : req._id);
                            if (!activitiesMap[req._id]) {
                              setActivitiesMap(function(prev) { return Object.assign({}, prev, { [req._id]: req.activities || [] }); });
                            }
                          }} style={{ ...s.expandBtn, color: '#8e44ad', borderColor: '#8e44ad' }}>
                            {activitiesOpen === req._id ? 'Hide Activities' : 'Activities (' + (req.activities ? req.activities.filter(function(a) { return a.isActive; }).length : 0) + ')'}
                          </button>
                        </div>

                        {/* Activities Panel */}
                        {activitiesOpen === req._id && (
                          <ActivityPanel
                            requestId={req._id}
                            activities={activitiesMap[req._id] || req.activities || []}
                            onUpdate={function(updated) {
                              setActivitiesMap(function(prev) { return Object.assign({}, prev, { [req._id]: updated }); });
                              setRequests(function(prev) { return prev.map(function(r) { return r._id === req._id ? Object.assign({}, r, { activities: updated }) : r; }); });
                            }}
                          />
                        )}

                        {/* Invoice Section */}
                        {req.payment && req.payment.healerShareAmount > 0 && (
                          <div style={{ marginTop: '12px', background: '#f9faf7', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '14px 16px' }}>
                            <div style={{ fontSize: '11px', fontWeight: '700', color: '#4a8f82', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '10px' }}>Your Payments</div>
                            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '10px', fontSize: '12px', color: '#555' }}>
                              <span>Sessions done: <strong>{req.totalSessionsCompleted || 0}</strong></span>
                              <span>Invoiced: <strong>{(req.healerPayments || []).reduce(function(s, p) { return s + (p.sessionsClaimed || 0); }, 0)}</strong></span>
                              <span>Available: <strong style={{ color: '#27ae60' }}>{Math.max(0, (req.totalSessionsCompleted || 0) - (req.healerPayments || []).reduce(function(s, p) { return s + (p.sessionsClaimed || 0); }, 0))}</strong></span>
                              <span>Rate: <strong>₹{req.sessions_planned ? Math.round((req.payment.healerShareAmount || 0) / req.sessions_planned).toLocaleString('en-IN') : '—'}/session</strong></span>
                            </div>
                            {(req.healerPayments || []).length > 0 && (
                              <div style={{ marginBottom: '10px' }}>
                                {req.healerPayments.map(function(hp, hIdx) {
                                  return (
                                    <div key={hIdx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 8px', background: hp.status === 'settled' ? '#eafaf1' : '#fef9e7', borderRadius: '4px', marginBottom: '4px', fontSize: '12px' }}>
                                      <span>{hp.paymentNumber} — {hp.sessionsClaimed} session(s) — ₹{(hp.amountClaimed || 0).toLocaleString('en-IN')}</span>
                                      <span style={{ background: hp.status === 'settled' ? '#27ae60' : '#d4ac0d', color: '#fff', borderRadius: '4px', padding: '1px 6px', fontSize: '11px' }}>{hp.status}</span>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                            {Math.max(0, (req.totalSessionsCompleted || 0) - (req.healerPayments || []).reduce(function(s, p) { return s + (p.sessionsClaimed || 0); }, 0)) > 0 && (
                              <button onClick={function() { setInvoiceModal(req._id); setInvoiceForm({ sessionsClaimed: 1, notes: '' }); }} style={{ fontSize: '12px', padding: '6px 14px', background: '#4a8f82', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                                Raise Invoice
                              </button>
                            )}
                          </div>
                        )}

                        {/* Invoice Modal */}
                        {invoiceModal === req._id && (
                          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
                            <div style={{ background: '#fff', borderRadius: '8px', padding: '28px', maxWidth: '400px', width: '100%', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
                              <div style={{ fontSize: '16px', fontWeight: '700', color: '#1a1a2e', marginBottom: '6px' }}>Raise Invoice</div>
                              <div style={{ fontSize: '12px', color: '#999', marginBottom: '20px' }}>{req.condition}</div>
                              <div style={{ marginBottom: '14px' }}>
                                <label style={{ fontSize: '11px', fontWeight: '700', color: '#666', textTransform: 'uppercase', letterSpacing: '1px', display: 'block', marginBottom: '6px' }}>
                                  Sessions to Invoice (max {Math.max(0, (req.totalSessionsCompleted || 0) - (req.healerPayments || []).reduce(function(s, p) { return s + (p.sessionsClaimed || 0); }, 0))})
                                </label>
                                <input type="number" min="1" max={Math.max(0, (req.totalSessionsCompleted || 0) - (req.healerPayments || []).reduce(function(s, p) { return s + (p.sessionsClaimed || 0); }, 0))} value={invoiceForm.sessionsClaimed} onChange={function(e) { setInvoiceForm({ ...invoiceForm, sessionsClaimed: parseInt(e.target.value) || 1 }); }} style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px', boxSizing: 'border-box' }} />
                              </div>
                              {req.sessions_planned && req.payment.healerShareAmount > 0 && (
                                <div style={{ background: '#eafaf1', padding: '10px 12px', borderRadius: '6px', marginBottom: '14px', fontSize: '13px', color: '#1a5c38' }}>
                                  Estimated amount: <strong>₹{(Math.round((req.payment.healerShareAmount || 0) / req.sessions_planned) * (invoiceForm.sessionsClaimed || 0)).toLocaleString('en-IN')}</strong>
                                </div>
                              )}
                              <div style={{ marginBottom: '20px' }}>
                                <label style={{ fontSize: '11px', fontWeight: '700', color: '#666', textTransform: 'uppercase', letterSpacing: '1px', display: 'block', marginBottom: '6px' }}>Notes (optional)</label>
                                <input value={invoiceForm.notes} onChange={function(e) { setInvoiceForm({ ...invoiceForm, notes: e.target.value }); }} style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px', boxSizing: 'border-box' }} placeholder="Any notes for admin..." />
                              </div>
                              <div style={{ display: 'flex', gap: '10px' }}>
                                <button onClick={function() { handleRaiseInvoice(req._id); }} disabled={raisingInvoice} style={{ flex: 1, padding: '10px', background: '#4a8f82', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: '700', fontSize: '13px' }}>
                                  {raisingInvoice ? 'Submitting...' : 'Submit Invoice'}
                                </button>
                                <button onClick={function() { setInvoiceModal(null); }} style={{ padding: '10px 16px', background: '#f5f5f5', color: '#333', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' }}>Cancel</button>
                              </div>
                            </div>
                          </div>
                        )}

 
                        {expandedPatient === req._id && (
                          <div style={s.sessionHistory}>
                            <div style={s.sessionHistoryTitle}>Session History ({req.sessions ? req.sessions.length : 0} sessions)</div>
                            {!req.sessions || req.sessions.length === 0 ? (
                              <div style={{ padding: '16px', color: '#999', fontSize: '13px' }}>No sessions recorded yet.</div>
                            ) : req.sessions.map(function(session, index) {
                              return (
                                <div key={index} style={s.sessionHistoryItem}>
                                  <div style={s.sessionHistoryLeft}>
                                    <div style={s.sessionNum}>#{index + 1}</div>
                                    <div>
                                      <div style={s.sessionDate}>{new Date(session.date).toLocaleDateString('en-IN', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' })}</div>
                                      <div style={s.sessionDuration}>{session.duration} minutes</div>
                                    </div>
                                  </div>
                                  <div style={{ flex: 1 }}>
                                    {(session.session_notes_internal || session.healerRemarks) && (
                                      <div style={{ background: '#fef9e7', borderLeft: '3px solid #d4ac0d', padding: '6px 10px', borderRadius: '4px', marginBottom: '6px' }}>
                                        <div style={{ fontSize: '9px', fontWeight: '700', color: '#7D6608', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '2px' }}>Internal</div>
                                        <div style={{ fontSize: '13px', color: '#555', lineHeight: '1.6' }}>{session.session_notes_internal || session.healerRemarks}</div>
                                      </div>
                                    )}
                                    {session.session_notes_patient && (
                                      <div style={{ background: '#eafaf1', borderLeft: '3px solid #27ae60', padding: '6px 10px', borderRadius: '4px' }}>
                                        <div style={{ fontSize: '9px', fontWeight: '700', color: '#1a5c38', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '2px' }}>Patient Notes</div>
                                        <div style={{ fontSize: '13px', color: '#555', lineHeight: '1.6' }}>{session.session_notes_patient}</div>
                                      </div>
                                    )}
                                    {!session.session_notes_internal && !session.healerRemarks && !session.session_notes_patient && (!session.scanningResults || session.scanningResults.length === 0) && (
                                      <div style={{ fontSize: '13px', color: '#999' }}>No remarks</div>
                                    )}
                                    {session.scanningResults && session.scanningResults.length > 0 && (function() {
                                      var scanKey = 'active_' + session._id;
                                      var isOpen = !!expandedScanSessions[scanKey];
                                      var groups = { major: [], minor: [], organs: [] };
                                      session.scanningResults.forEach(function(r) { if (groups[r.group]) groups[r.group].push(r); });
                                      var ENERGY_COLOR = { C: '#e67e22', D: '#7f8c8d', N: '#27ae60' };
                                      var ACTIV_COLOR  = { U: '#2980b9', O: '#e74c3c', N: '#27ae60' };
                                      var GROUP_LABELS = { major: 'Major Chakras', minor: 'Minor Chakras', organs: 'Organs & Mini' };
                                      return (
                                        <div style={{ marginTop: '8px', border: '1px solid #e0f0ec', borderRadius: '6px', overflow: 'hidden' }}>
                                          <button type="button" onClick={function() { setExpandedScanSessions(function(prev) { return Object.assign({}, prev, { [scanKey]: !prev[scanKey] }); }); }}
                                            style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 12px', background: '#1a6b5a', border: 'none', cursor: 'pointer' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                              <span style={{ fontSize: '12px', fontWeight: '700', color: '#fff', fontFamily: "'Raleway', sans-serif", letterSpacing: '0.5px' }}>Chakra Scanning Results</span>
                                              <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.6)' }}>{session.scanningResults.length} readings</span>
                                            </div>
                                            <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)' }}>{isOpen ? '▲' : '▼'}</span>
                                          </button>
                                          {isOpen && (
                                            <div style={{ overflowX: 'auto' }}>
                                              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', minWidth: '480px' }}>
                                                <thead>
                                                  <tr style={{ background: '#f5f8f7' }}>
                                                    <th style={{ padding: '6px 10px', textAlign: 'left', fontWeight: '700', color: '#555', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.8px', borderBottom: '1px solid #e0e0e0' }}>Chakra</th>
                                                    <th style={{ padding: '6px 8px', textAlign: 'center', fontWeight: '700', color: '#e67e22', fontSize: '10px', borderBottom: '1px solid #e0e0e0' }}>Energy Before</th>
                                                    <th style={{ padding: '6px 8px', textAlign: 'center', fontWeight: '700', color: '#27ae60', fontSize: '10px', borderBottom: '1px solid #e0e0e0' }}>Energy After</th>
                                                    <th style={{ padding: '6px 8px', textAlign: 'center', fontWeight: '700', color: '#2980b9', fontSize: '10px', borderBottom: '1px solid #e0e0e0' }}>Activation Before</th>
                                                    <th style={{ padding: '6px 8px', textAlign: 'center', fontWeight: '700', color: '#8e44ad', fontSize: '10px', borderBottom: '1px solid #e0e0e0' }}>Activation After</th>
                                                  </tr>
                                                </thead>
                                                <tbody>
                                                  {['major', 'minor', 'organs'].map(function(gk) {
                                                    if (!groups[gk] || groups[gk].length === 0) return null;
                                                    return [
                                                      <tr key={gk + '_header'}>
                                                        <td colSpan="5" style={{ padding: '5px 10px', background: '#eef7f5', fontSize: '10px', fontWeight: '700', color: '#1a6b5a', textTransform: 'uppercase', letterSpacing: '0.8px' }}>{GROUP_LABELS[gk]}</td>
                                                      </tr>
                                                    ].concat(groups[gk].map(function(r, ri) {
                                                      function pill(val, colorMap) {
                                                        if (!val) return <span style={{ color: '#ccc', fontSize: '11px' }}>—</span>;
                                                        return <span style={{ display: 'inline-block', width: '24px', height: '24px', lineHeight: '24px', borderRadius: '50%', background: colorMap[val] || '#ccc', color: '#fff', fontSize: '11px', fontWeight: '700', textAlign: 'center' }}>{val}</span>;
                                                      }
                                                      return (
                                                        <tr key={gk + ri} style={{ background: ri % 2 === 0 ? '#fff' : '#fafafa', borderBottom: '1px solid #f0f0f0' }}>
                                                          <td style={{ padding: '6px 10px', color: '#333', fontWeight: '500' }}>{r.chakra}</td>
                                                          <td style={{ padding: '6px 8px', textAlign: 'center' }}>{pill(r.energyBefore, ENERGY_COLOR)}</td>
                                                          <td style={{ padding: '6px 8px', textAlign: 'center' }}>{pill(r.energyAfter, ENERGY_COLOR)}</td>
                                                          <td style={{ padding: '6px 8px', textAlign: 'center' }}>{pill(r.activationBefore, ACTIV_COLOR)}</td>
                                                          <td style={{ padding: '6px 8px', textAlign: 'center' }}>{pill(r.activationAfter, ACTIV_COLOR)}</td>
                                                        </tr>
                                                      );
                                                    }));
                                                  })}
                                                </tbody>
                                              </table>
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })()}
                                  </div>
                                  <span style={{ ...s.badge, background: session.status === 'completed' ? '#eafaf1' : '#fef9e7', color: session.status === 'completed' ? '#27ae60' : '#d4ac0d' }}>{session.status}</span>
                                  {/* Per-session feedback */}
                                  {session.status === 'completed' && (function() {
                                    var fbRequests = session.feedbackRequests || [];
                                    var hasPending = fbRequests.some(function(fr) { return fr.status === 'pending'; });
                                    var submitted = fbRequests.filter(function(fr) { return fr.status === 'submitted'; });
                                    var sessionDateStr = new Date(session.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
                                    return (
                                      <div style={{ marginTop: '8px' }}>
                                        {!hasPending && (
                                          <button onClick={function() { handleRequestSessionFeedback(req._id, index, sessionDateStr, req.patient ? req.patient.firstName : ''); }}
                                            style={{ fontSize: '11px', padding: '4px 12px', background: '#2980b9', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontFamily: "'Raleway', sans-serif", fontWeight: '700', letterSpacing: '0.5px' }}>
                                            Request Feedback
                                          </button>
                                        )}
                                        {hasPending && (
                                          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                                            <span style={{ fontSize: '11px', color: '#d4ac0d', fontWeight: '700' }}>Feedback Pending</span>
                                            <button onClick={function() { handleRecallSessionFeedback(req._id, index, sessionDateStr); }}
                                              style={{ fontSize: '10px', padding: '3px 8px', background: 'transparent', color: '#e74c3c', border: '1px solid #e74c3c', borderRadius: '4px', cursor: 'pointer', fontFamily: "'Raleway', sans-serif", fontWeight: '700' }}>
                                              Recall
                                            </button>
                                          </div>
                                        )}
                                        {submitted.map(function(fr, fi) {
                                          return (
                                            <div key={fi} style={{ marginTop: '6px', background: '#eafaf1', borderLeft: '3px solid #27ae60', borderRadius: '4px', padding: '6px 10px' }}>
                                              <div style={{ fontSize: '9px', fontWeight: '700', color: '#1a5c38', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '2px' }}>Patient Feedback</div>
                                              <div style={{ fontSize: '12px', color: '#333', lineHeight: '1.5' }}>{fr.submittedText}</div>
                                              <div style={{ fontSize: '10px', color: '#999', marginTop: '3px' }}>{new Date(fr.submittedAt).toLocaleDateString('en-IN')}</div>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    );
                                  })()}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* COMPLETED TAB */}
              {activeTab === 'completed' && (
                <div style={s.patientList}>
                  {completed.length === 0 ? (
                    <div style={s.emptyCard}>
                      <div style={s.emptyTitle}>No completed cases yet</div>
                      <div style={s.emptySub}>Completed cases will appear here once marked as done.</div>
                    </div>
                  ) : completed.map(function(req) {
                    return (
                      <div key={req._id} style={s.patientCard}>
                        <div style={s.patientCardHeader}>
                          <div style={s.patientCardLeft}>
                            <div style={s.patientAvatarLg}>
                              {req.patient ? req.patient.firstName.charAt(0) : (req.manualPatientName ? req.manualPatientName.charAt(0) : '')}
                              {req.patient ? req.patient.lastName.charAt(0) : (req.manualPatientName ? (req.manualPatientName.split(' ')[1] || '').charAt(0) : '')}
                            </div>
                            <div>
                              <div style={s.patientCardName}>{req.patient ? (req.patient.firstName + ' ' + req.patient.lastName) : (req.manualPatientName || '')}</div>
                              {req.patient && req.patient.showHealerNameToPatient && req.patient.email ? (
                                <div style={s.patientCardContact}>{req.patient.email}</div>
                              ) : null}
                              <div style={s.patientCardCondition}>{req.condition}</div>
                            </div>
                          </div>
                          <div style={s.patientCardRight}>
                            <div style={s.patientCardStats}>
                              <div style={s.patientCardStat}>
                                <div style={s.patientCardStatNum}>{req.totalSessionsCompleted}</div>
                                <div style={s.patientCardStatLabel}>Total Sessions</div>
                              </div>
                            </div>
                            <span style={{ ...s.badge, background: '#f0f0f0', color: '#666' }}>Completed</span>
                          </div>
                        </div>
                        {/* Completed case invoice summary */}
                        {req.payment && req.payment.healerShareAmount > 0 && (
                          <div style={{ marginTop: '10px', background: '#f9faf7', border: '1px solid #e0e0e0', borderRadius: '6px', padding: '12px 14px', fontSize: '12px', color: '#555' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                              <div style={{ fontSize: '11px', fontWeight: '700', color: '#4a8f82', textTransform: 'uppercase', letterSpacing: '1.5px' }}>Payment Summary</div>
                              <button onClick={function() { printHealerStatement(req); }} style={{ fontSize: '10px', padding: '4px 10px', background: '#4a8f82', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: '700' }}>
                                Download Statement
                              </button>
                            </div>
                            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                              <span>Total entitlement: <strong>₹{(req.payment.healerShareAmount || 0).toLocaleString('en-IN')}</strong></span>
                              <span>Settled: <strong style={{ color: req.payment.settledToHealer ? '#27ae60' : '#d4ac0d' }}>{req.payment.settledToHealer ? '✓ Fully' : 'Partial/Pending'}</strong></span>
                            </div>
                            {(req.healerPayments || []).length > 0 && (
                              <div style={{ marginTop: '8px' }}>
                                {req.healerPayments.map(function(hp, hIdx) {
                                  return (
                                    <div key={hIdx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', borderBottom: '1px solid #f0f0f0', fontSize: '12px' }}>
                                      <span style={{ color: '#666' }}>{hp.paymentNumber} — {hp.sessionsClaimed} session(s)</span>
                                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                        <span>₹{(hp.amountClaimed || 0).toLocaleString('en-IN')}</span>
                                        <span style={{ background: hp.status === 'settled' ? '#27ae60' : '#d4ac0d', color: '#fff', borderRadius: '4px', padding: '1px 6px', fontSize: '10px' }}>{hp.status}</span>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        )}

                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '10px' }}>
                          <button onClick={function() { setExpandedPatient(expandedPatient === req._id ? null : req._id); }} style={s.expandBtn}>
                            {expandedPatient === req._id ? 'Hide History' : 'View Session History'}
                          </button>
                          <button onClick={function() {
                            setActivitiesOpen(activitiesOpen === req._id ? null : req._id);
                            if (!activitiesMap[req._id]) {
                              setActivitiesMap(function(prev) { return Object.assign({}, prev, { [req._id]: req.activities || [] }); });
                            }
                          }} style={{ ...s.expandBtn, color: '#8e44ad', borderColor: '#8e44ad' }}>
                            {activitiesOpen === req._id ? 'Hide Activities' : 'Activities (' + (req.activities ? req.activities.filter(function(a) { return a.isActive; }).length : 0) + ')'}
                          </button>
                        </div>

                        {/* Activities Panel — completed cases */}
                        {activitiesOpen === req._id && (
                          <ActivityPanel
                            requestId={req._id}
                            activities={activitiesMap[req._id] || req.activities || []}
                            onUpdate={function(updated) {
                              setActivitiesMap(function(prev) { return Object.assign({}, prev, { [req._id]: updated }); });
                              setRequests(function(prev) { return prev.map(function(r) { return r._id === req._id ? Object.assign({}, r, { activities: updated }) : r; }); });
                            }}
                          />
                        )}

                        {expandedPatient === req._id && (
                          <div style={s.sessionHistory}>
                            <div style={s.sessionHistoryTitle}>Session History</div>
                            {req.sessions && req.sessions.map(function(session, index) {
                              return (
                                <div key={index} style={s.sessionHistoryItem}>
                                  <div style={s.sessionHistoryLeft}>
                                    <div style={s.sessionNum}>#{index + 1}</div>
                                    <div>
                                      <div style={s.sessionDate}>{new Date(session.date).toLocaleDateString('en-IN')}</div>
                                      <div style={s.sessionDuration}>{session.duration} mins</div>
                                    </div>
                                  </div>
                                  <div style={{ flex: 1 }}>
                                    {(session.session_notes_internal || session.healerRemarks) && (
                                      <div style={{ background: '#fef9e7', borderLeft: '3px solid #d4ac0d', padding: '6px 10px', borderRadius: '4px', marginBottom: '6px' }}>
                                        <div style={{ fontSize: '9px', fontWeight: '700', color: '#7D6608', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '2px' }}>Internal</div>
                                        <div style={{ fontSize: '13px', color: '#555', lineHeight: '1.6' }}>{session.session_notes_internal || session.healerRemarks}</div>
                                      </div>
                                    )}
                                    {session.session_notes_patient && (
                                      <div style={{ background: '#eafaf1', borderLeft: '3px solid #27ae60', padding: '6px 10px', borderRadius: '4px' }}>
                                        <div style={{ fontSize: '9px', fontWeight: '700', color: '#1a5c38', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '2px' }}>Patient Notes</div>
                                        <div style={{ fontSize: '13px', color: '#555', lineHeight: '1.6' }}>{session.session_notes_patient}</div>
                                      </div>
                                    )}
                                    {!session.session_notes_internal && !session.healerRemarks && !session.session_notes_patient && (
                                      <div style={{ fontSize: '13px', color: '#999' }}>No remarks</div>
                                    )}
                                    {session.scanningResults && session.scanningResults.length > 0 && (function() {
                                      var scanKey = 'completed_' + session._id;
                                      var isOpen = !!expandedScanSessions[scanKey];
                                      var groups = { major: [], minor: [], organs: [] };
                                      session.scanningResults.forEach(function(r) { if (groups[r.group]) groups[r.group].push(r); });
                                      var ENERGY_COLOR = { C: '#e67e22', D: '#7f8c8d', N: '#27ae60' };
                                      var ACTIV_COLOR  = { U: '#2980b9', O: '#e74c3c', N: '#27ae60' };
                                      var GROUP_LABELS = { major: 'Major Chakras', minor: 'Minor Chakras', organs: 'Organs & Mini' };
                                      return (
                                        <div style={{ marginTop: '8px', border: '1px solid #e0f0ec', borderRadius: '6px', overflow: 'hidden' }}>
                                          <button type="button" onClick={function() { setExpandedScanSessions(function(prev) { return Object.assign({}, prev, { [scanKey]: !prev[scanKey] }); }); }}
                                            style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 12px', background: '#1a6b5a', border: 'none', cursor: 'pointer' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                              <span style={{ fontSize: '11px', fontWeight: '700', color: '#fff', letterSpacing: '0.5px' }}>Chakra Scanning Results</span>
                                              <span style={{ fontSize: '10px', color: 'rgba(255,255,255,0.6)' }}>{session.scanningResults.length} readings</span>
                                            </div>
                                            <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)' }}>{isOpen ? '▲' : '▼'}</span>
                                          </button>
                                          {isOpen && (
                                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                                              <thead>
                                                <tr style={{ background: '#f0f7f5' }}>
                                                  <th style={{ padding: '4px 8px', textAlign: 'left', color: '#555', fontWeight: '700', borderBottom: '1px solid #ddd' }}>Chakra</th>
                                                  <th style={{ padding: '4px 8px', textAlign: 'center', color: '#555', fontWeight: '700', borderBottom: '1px solid #ddd' }}>E.Before</th>
                                                  <th style={{ padding: '4px 8px', textAlign: 'center', color: '#555', fontWeight: '700', borderBottom: '1px solid #ddd' }}>E.After</th>
                                                  <th style={{ padding: '4px 8px', textAlign: 'center', color: '#555', fontWeight: '700', borderBottom: '1px solid #ddd' }}>A.Before</th>
                                                  <th style={{ padding: '4px 8px', textAlign: 'center', color: '#555', fontWeight: '700', borderBottom: '1px solid #ddd' }}>A.After</th>
                                                </tr>
                                              </thead>
                                              <tbody>
                                                {['major','minor','organs'].map(function(gk) {
                                                  if (!groups[gk] || groups[gk].length === 0) return null;
                                                  return [
                                                    <tr key={gk + '_head'}>
                                                      <td colSpan={5} style={{ padding: '3px 8px', background: '#e8f4f0', fontSize: '10px', fontWeight: '700', color: '#1a6b5a', textTransform: 'uppercase', letterSpacing: '0.8px' }}>{GROUP_LABELS[gk]}</td>
                                                    </tr>
                                                  ].concat(groups[gk].map(function(r, ri) {
                                                    function pill(val, colorMap) {
                                                      if (!val) return <span style={{ color: '#ccc' }}>—</span>;
                                                      return <span style={{ display: 'inline-block', width: '20px', height: '20px', borderRadius: '50%', background: colorMap[val] || '#eee', color: '#fff', fontSize: '10px', fontWeight: '700', lineHeight: '20px', textAlign: 'center' }}>{val}</span>;
                                                    }
                                                    return (
                                                      <tr key={gk + '_' + ri} style={{ background: ri % 2 === 0 ? '#fff' : '#fafafa' }}>
                                                        <td style={{ padding: '4px 8px', color: '#333' }}>{r.chakra}</td>
                                                        <td style={{ padding: '4px 8px', textAlign: 'center' }}>{pill(r.energyBefore, ENERGY_COLOR)}</td>
                                                        <td style={{ padding: '4px 8px', textAlign: 'center' }}>{pill(r.energyAfter, ENERGY_COLOR)}</td>
                                                        <td style={{ padding: '4px 8px', textAlign: 'center' }}>{pill(r.activationBefore, ACTIV_COLOR)}</td>
                                                        <td style={{ padding: '4px 8px', textAlign: 'center' }}>{pill(r.activationAfter, ACTIV_COLOR)}</td>
                                                      </tr>
                                                    );
                                                  }));
                                                })}
                                              </tbody>
                                            </table>
                                          )}
                                        </div>
                                      );
                                    })()}
                                  </div>
                                  <span style={{ ...s.badge, background: '#eafaf1', color: '#27ae60' }}>{session.status}</span>
                                  {/* Per-session feedback */}
                                  {(function() {
                                    var fbRequests = session.feedbackRequests || [];
                                    var hasPending = fbRequests.some(function(fr) { return fr.status === 'pending'; });
                                    var submitted = fbRequests.filter(function(fr) { return fr.status === 'submitted'; });
                                    var sessionDateStr = new Date(session.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
                                    return (
                                      <div style={{ marginTop: '8px' }}>
                                        {!hasPending && (
                                          <button onClick={function() { handleRequestSessionFeedback(req._id, index, sessionDateStr, req.patient ? req.patient.firstName : ''); }}
                                            style={{ fontSize: '11px', padding: '4px 12px', background: '#2980b9', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontFamily: "'Raleway', sans-serif", fontWeight: '700', letterSpacing: '0.5px' }}>
                                            Request Feedback
                                          </button>
                                        )}
                                        {hasPending && (
                                          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                                            <span style={{ fontSize: '11px', color: '#d4ac0d', fontWeight: '700' }}>Feedback Pending</span>
                                            <button onClick={function() { handleRecallSessionFeedback(req._id, index, sessionDateStr); }}
                                              style={{ fontSize: '10px', padding: '3px 8px', background: 'transparent', color: '#e74c3c', border: '1px solid #e74c3c', borderRadius: '4px', cursor: 'pointer', fontFamily: "'Raleway', sans-serif", fontWeight: '700' }}>
                                              Recall
                                            </button>
                                          </div>
                                        )}
                                        {submitted.map(function(fr, fi) {
                                          return (
                                            <div key={fi} style={{ marginTop: '6px', background: '#eafaf1', borderLeft: '3px solid #27ae60', borderRadius: '4px', padding: '6px 10px' }}>
                                              <div style={{ fontSize: '9px', fontWeight: '700', color: '#1a5c38', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '2px' }}>Patient Feedback</div>
                                              <div style={{ fontSize: '12px', color: '#333', lineHeight: '1.5' }}>{fr.submittedText}</div>
                                              <div style={{ fontSize: '10px', color: '#999', marginTop: '3px' }}>{new Date(fr.submittedAt).toLocaleDateString('en-IN')}</div>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    );
                                  })()}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* SESSIONS TAB */}
              {activeTab === 'sessions' && canViewSessions && (
                <div style={s.patientList}>
                  {sessions.filter(function(s) { return s.status === 'upcoming'; }).length === 0 ? (
                    <div style={s.emptyCard}>
                      <div style={s.emptyTitle}>No upcoming sessions</div>
                      <div style={s.emptySub}>Check back soon for upcoming sessions and events.</div>
                    </div>
                  ) : sessions.filter(function(s) { return s.status === 'upcoming'; }).map(function(session) {
                    return (
                      <div key={session._id} style={s.patientCard}>
                        <div style={s.patientCardHeader}>
                          <div style={s.patientCardLeft}>
                            <div style={{ width: '48px', height: '48px', borderRadius: '8px', background: 'linear-gradient(135deg, #071e12, #0c2617)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                              <span style={{ fontFamily: "'Raleway', sans-serif", fontSize: '18px', fontWeight: '700', color: '#8DC63F', lineHeight: 1 }}>{new Date(session.date).getDate()}</span>
                              <span style={{ fontSize: '9px', color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', letterSpacing: '1px' }}>{new Date(session.date).toLocaleString('en-IN', { month: 'short' })}</span>
                            </div>
                            <div>
                              <div style={s.patientCardName}>{session.title}</div>
                              <div style={s.patientCardContact}>{session.startTime} to {session.endTime} | {session.venue}</div>
                              <div style={s.patientCardCondition}>{session.type ? session.type.replace(/_/g, ' ') : ''}</div>
                            </div>
                          </div>
                          <div style={s.patientCardRight}>
                            <div style={s.patientCardStats}>
                              <div style={s.patientCardStat}>
                                <div style={s.patientCardStatNum}>{session.currentEnrollments || 0}/{session.maxParticipants}</div>
                                <div style={s.patientCardStatLabel}>Enrolled</div>
                              </div>
                            </div>
                            <span style={{ ...s.badge, background: '#eafaf1', color: '#27ae60' }}>Upcoming</span>
                          </div>
                        </div>
                        {session.description && (
                          <div style={s.patientCardDesc}>{session.description}</div>
                        )}
                        <div style={s.patientCardActions}>
                          <button onClick={function() { handleEnroll(session._id, session.title); }}
                            style={s.addSessionBtn}
                            disabled={enrolling === session._id}>
                            {enrolling === session._id ? 'Enrolling...' : 'Enroll'}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* PERFORMANCE TAB */}
              {activeTab === 'performance' && (function() {
                var feedbackList = requests.filter(function(r) { return r.patientFeedback && r.patientFeedback.trim(); });
                var ratedList = requests.filter(function(r) { return r.patientRating && r.patientRating > 0; });
                var avgRating = ratedList.length > 0 ? (ratedList.reduce(function(sum, r) { return sum + r.patientRating; }, 0) / ratedList.length).toFixed(1) : null;
                var completionRate = requests.length > 0 ? Math.round(completed.length / requests.length * 100) : 0;
                var avgSessions = completed.length > 0 ? (completed.reduce(function(sum, r) { return sum + (r.totalSessionsCompleted || 0); }, 0) / completed.length).toFixed(1) : 0;
                var conditionMap = {};
                requests.forEach(function(r) {
                  var c = r.condition || 'Other';
                  conditionMap[c] = (conditionMap[c] || 0) + 1;
                });
                var topConditions = Object.entries(conditionMap).sort(function(a, b) { return b[1] - a[1]; }).slice(0, 5);

                // Invoice stats — healer's own invoices only (amountClaimed by healer, not patient payments)
                var allInvoices = requests.reduce(function(acc, r) {
                  return acc.concat((r.healerPayments || []).map(function(p) {
                    return Object.assign({}, p, {
                      condition: r.condition,
                      patientName: r.patient ? r.patient.firstName + ' ' + r.patient.lastName : (r.manualPatientName || 'Unknown')
                    });
                  }));
                }, []);
                var invoicesRaised = allInvoices.length;
                var invoicesPaid = allInvoices.filter(function(p) { return p.status === 'settled'; }).length;
                var invoicesPending = allInvoices.filter(function(p) { return p.status === 'pending'; }).length;
                var totalReceived = allInvoices.filter(function(p) { return p.status === 'settled'; }).reduce(function(s, p) { return s + (p.amountClaimed || 0); }, 0);
                var totalPending = allInvoices.filter(function(p) { return p.status === 'pending'; }).reduce(function(s, p) { return s + (p.amountClaimed || 0); }, 0);

                return (
                  <div>
                    {/* Summary cards */}
                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px' }}>
                      {[
                        { label: 'Total Cases', value: requests.length, color: '#4a8f82' },
                        { label: 'Active Cases', value: active.length, color: '#27ae60' },
                        { label: 'Completed Cases', value: completed.length, color: '#2980b9' },
                        { label: 'Total Sessions', value: totalSessions, color: '#8e44ad' }
                      ].map(function(stat) {
                        return (
                          <div key={stat.label} style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: '10px', padding: '20px 16px', textAlign: 'center' }}>
                            <div style={{ fontSize: '28px', fontWeight: '700', color: stat.color, fontFamily: "'Raleway', sans-serif" }}>{stat.value}</div>
                            <div style={{ fontSize: '11px', color: '#888', textTransform: 'uppercase', letterSpacing: '1.2px', marginTop: '4px', fontWeight: '600' }}>{stat.label}</div>
                          </div>
                        );
                      })}
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '20px', marginBottom: '24px' }}>
                      {/* Completion & avg sessions */}
                      <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: '10px', padding: '20px' }}>
                        <div style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1.5px', color: '#888', marginBottom: '16px' }}>Healing Outcomes</div>
                        <div style={{ marginBottom: '16px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#555', marginBottom: '6px' }}>
                            <span>Completion Rate</span>
                            <strong style={{ color: completionRate >= 70 ? '#27ae60' : completionRate >= 40 ? '#d4ac0d' : '#e74c3c' }}>{completionRate}%</strong>
                          </div>
                          <div style={{ background: '#f0f0f0', borderRadius: '4px', height: '8px', overflow: 'hidden' }}>
                            <div style={{ width: completionRate + '%', height: '100%', background: completionRate >= 70 ? '#27ae60' : completionRate >= 40 ? '#d4ac0d' : '#e74c3c', borderRadius: '4px', transition: 'width 0.6s ease' }}></div>
                          </div>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#555', padding: '10px 0', borderBottom: '1px solid #f0f0f0' }}>
                          <span>Avg Sessions per Case</span>
                          <strong>{avgSessions}</strong>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#555', padding: '10px 0' }}>
                          <span>Feedback Received</span>
                          <strong>{feedbackList.length} / {requests.length}</strong>
                        </div>
                        {avgRating && (
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#555', padding: '10px 0', borderTop: '1px solid #f0f0f0' }}>
                            <span>Average Rating</span>
                            <strong style={{ color: '#d4ac0d' }}>{'★'.repeat(Math.round(avgRating))} {avgRating} / 5</strong>
                          </div>
                        )}
                      </div>

                      {/* Top conditions */}
                      <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: '10px', padding: '20px' }}>
                        <div style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1.5px', color: '#888', marginBottom: '16px' }}>Conditions Treated</div>
                        {topConditions.length === 0 ? (
                          <div style={{ color: '#bbb', fontSize: '13px' }}>No cases yet</div>
                        ) : topConditions.map(function(entry) {
                          var pct = requests.length > 0 ? Math.round(entry[1] / requests.length * 100) : 0;
                          return (
                            <div key={entry[0]} style={{ marginBottom: '12px' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#555', marginBottom: '4px' }}>
                                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingRight: '8px' }}>{entry[0]}</span>
                                <span style={{ whiteSpace: 'nowrap' }}>{entry[1]} case{entry[1] > 1 ? 's' : ''} ({pct}%)</span>
                              </div>
                              <div style={{ background: '#f0f0f0', borderRadius: '4px', height: '6px', overflow: 'hidden' }}>
                                <div style={{ width: pct + '%', height: '100%', background: '#4a8f82', borderRadius: '4px' }}></div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Invoice Summary */}
                    <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: '10px', padding: '20px', marginBottom: '20px' }}>
                      <div style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1.5px', color: '#888', marginBottom: '16px' }}>My Invoices</div>
                      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)', gap: '12px', marginBottom: invoicesRaised > 0 ? '20px' : '0' }}>
                        {[
                          { label: 'Total Raised', value: invoicesRaised, color: '#2980b9', suffix: '' },
                          { label: 'Settled', value: invoicesPaid, color: '#27ae60', suffix: '' },
                          { label: 'Pending', value: invoicesPending, color: '#e67e22', suffix: '' },
                          { label: 'Amount Received', value: '₹' + totalReceived.toLocaleString('en-IN'), color: '#27ae60', suffix: '' },
                        ].map(function(stat) {
                          return (
                            <div key={stat.label} style={{ background: '#f9faf7', border: '1px solid #e8e8e8', borderRadius: '8px', padding: '14px', textAlign: 'center' }}>
                              <div style={{ fontSize: '22px', fontWeight: '700', color: stat.color, fontFamily: "'Raleway', sans-serif" }}>{stat.value}</div>
                              <div style={{ fontSize: '10px', color: '#999', textTransform: 'uppercase', letterSpacing: '1px', marginTop: '4px', fontWeight: '600' }}>{stat.label}</div>
                            </div>
                          );
                        })}
                      </div>
                      {totalPending > 0 && (
                        <div style={{ background: '#fef9e7', border: '1px solid #f0d9b5', borderRadius: '6px', padding: '10px 14px', fontSize: '13px', color: '#d4ac0d' }}>
                          <strong>₹{totalPending.toLocaleString('en-IN')}</strong> pending settlement from PWC
                        </div>
                      )}
                      {invoicesRaised === 0 && (
                        <div style={{ color: '#bbb', fontSize: '13px' }}>No invoices raised yet. You can raise an invoice from your active or completed cases.</div>
                      )}
                      {/* Invoice log */}
                      {allInvoices.length > 0 && (
                        <div style={{ marginTop: '16px' }}>
                          <div style={{ fontSize: '11px', fontWeight: '700', color: '#aaa', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '10px' }}>Invoice Log</div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            {allInvoices.slice().reverse().map(function(inv, i) {
                              return (
                                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: inv.status === 'settled' ? '#f0faf5' : '#fffbf5', border: '1px solid', borderColor: inv.status === 'settled' ? '#a9dfbf' : '#f0d9b5', borderRadius: '6px', flexWrap: 'wrap', gap: '8px' }}>
                                  <div>
                                    <div style={{ fontSize: '13px', fontWeight: '600', color: '#333' }}>{inv.patientName} — {inv.condition}</div>
                                    <div style={{ fontSize: '11px', color: '#999', marginTop: '2px' }}>{inv.sessionsClaimed} session{inv.sessionsClaimed > 1 ? 's' : ''} · Raised {new Date(inv.raisedAt).toLocaleDateString('en-IN')}{inv.status === 'settled' && inv.settledAt ? ' · Settled ' + new Date(inv.settledAt).toLocaleDateString('en-IN') : ''}</div>
                                  </div>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    {inv.amountClaimed > 0 && <span style={{ fontSize: '14px', fontWeight: '700', color: inv.status === 'settled' ? '#27ae60' : '#e67e22' }}>₹{inv.amountClaimed.toLocaleString('en-IN')}</span>}
                                    <span style={{ fontSize: '11px', fontWeight: '700', padding: '3px 10px', borderRadius: '10px', background: inv.status === 'settled' ? '#eafaf1' : '#fef9e7', color: inv.status === 'settled' ? '#27ae60' : '#e67e22' }}>
                                      {inv.status === 'settled' ? 'Settled' : 'Pending'}
                                    </span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Patient feedback */}
                    {feedbackList.length > 0 && (
                      <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: '10px', padding: '20px' }}>
                        <div style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1.5px', color: '#888', marginBottom: '16px' }}>Patient Feedback</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                          {feedbackList.map(function(req) {
                            return (
                              <div key={req._id} style={{ background: '#f9faf7', border: '1px solid #e0e0e0', borderLeft: '4px solid #4a8f82', borderRadius: '6px', padding: '12px 14px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px', flexWrap: 'wrap', gap: '6px' }}>
                                  <div>
                                    <span style={{ fontSize: '13px', fontWeight: '600', color: '#333' }}>{req.patient && req.patient.firstName} {req.patient && req.patient.lastName}</span>
                                    <span style={{ fontSize: '11px', color: '#999', marginLeft: '8px' }}>{req.condition}</span>
                                  </div>
                                  {req.patientRating > 0 && (
                                    <span style={{ color: '#d4ac0d', fontSize: '13px' }}>{'★'.repeat(req.patientRating)}{'☆'.repeat(5 - req.patientRating)}</span>
                                  )}
                                </div>
                                <div style={{ fontSize: '13px', color: '#555', lineHeight: '1.6', fontStyle: 'italic' }}>"{req.patientFeedback}"</div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {feedbackList.length === 0 && requests.length === 0 && (
                      <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: '10px', padding: '40px', textAlign: 'center', color: '#bbb' }}>
                        <div style={{ fontSize: '15px', marginBottom: '6px' }}>No data yet</div>
                        <div style={{ fontSize: '13px' }}>Performance metrics will appear once you have cases assigned.</div>
                      </div>
                    )}
                  </div>
                );
              })()}

            </div>
          )}

              {/* MY ACTIVITIES TAB */}
              {activeTab === 'my-activities' && (function() {
                var active = myPractActivities.filter(function(a) { return a.isActive; });
                var inactive = myPractActivities.filter(function(a) { return !a.isActive; });

                function getMyAssignee(act) {
                  if (!act.assignees) return null;
                  return act.assignees.find(function(a) { return true; }) || null; // backend already filters to own
                }

                function getScheduledDates(act) {
                  var start = new Date(act.startDate);
                  var end = act.endDate ? new Date(act.endDate) : new Date();
                  var dates = [];
                  var cur = new Date(start);
                  while (cur <= end) {
                    var ds = cur.toISOString().split('T')[0];
                    var dow = cur.getDay();
                    var ok = act.frequency === 'daily' ? true : ((act.daysOfWeek || []).length === 0 || act.daysOfWeek.includes(dow));
                    if (ok) dates.push(ds);
                    cur.setDate(cur.getDate() + 1);
                  }
                  return dates;
                }

                function compliance(act) {
                  var assignee = getMyAssignee(act);
                  if (!act.trackCompliance || !assignee) return null;
                  var today = new Date().toISOString().split('T')[0];
                  var pastDates = getScheduledDates(act).filter(function(d) { return d < today; });
                  var total = pastDates.length * (act.timesPerDay || 1);
                  if (total === 0) return null;
                  var done = (assignee.checkIns || []).filter(function(c) { return pastDates.includes(c.date); }).length;
                  return Math.min(100, Math.round((done / total) * 100));
                }

                function isCheckedIn(act, slotIndex) {
                  var assignee = getMyAssignee(act);
                  if (!assignee) return false;
                  var today = new Date().toISOString().split('T')[0];
                  return (assignee.checkIns || []).some(function(c) { return c.date === today && c.slotIndex === slotIndex && !c.missed; });
                }

                function isMissedToday(act, slotIndex) {
                  var assignee = getMyAssignee(act);
                  if (!assignee) return false;
                  var today = new Date().toISOString().split('T')[0];
                  return (assignee.checkIns || []).some(function(c) { return c.date === today && c.slotIndex === slotIndex && c.missed; });
                }

                function handleCheckin(act, slotIndex, missed) {
                  var today = new Date().toISOString().split('T')[0];
                  practitionerActivityCheckin(act._id, { date: today, slotIndex: slotIndex, missed: missed || false }).then(function(res) {
                    setMyPractActivities(function(prev) {
                      return prev.map(function(a) {
                        if (a._id !== act._id) return a;
                        var updatedAssignees = (a.assignees || []).map(function(x) {
                          return Object.assign({}, x, { checkIns: res.data.checkIns });
                        });
                        return Object.assign({}, a, { assignees: updatedAssignees });
                      });
                    });
                  }).catch(function() {});
                }

                function renderActivity(act) {
                  var comp = compliance(act);
                  var slots = act.slotLabels || ['Daily'];
                  return (
                    <div key={act._id} style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: '8px', marginBottom: '12px', overflow: 'hidden' }}>
                      <div style={{ padding: '14px 16px', borderBottom: '1px solid #f5f5f5' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <div>
                            <div style={{ fontFamily: "'Raleway', sans-serif", fontSize: '14px', fontWeight: '700', color: '#1a1a2e', marginBottom: '4px' }}>{act.title}</div>
                            {act.description && <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>{act.description}</div>}
                            <div style={{ fontSize: '11px', color: '#999' }}>{act.frequency === 'daily' ? 'Every day' : act.frequency === 'weekly' ? 'Weekly' : 'Custom days'} · {act.timesPerDay || 1}× {act.frequency === 'daily' ? 'per day' : 'per session'} · Starts {new Date(act.startDate).toLocaleDateString('en-IN')}{act.endDate ? ' → ' + new Date(act.endDate).toLocaleDateString('en-IN') : ''}</div>
                          </div>
                          {comp !== null && (
                            <div style={{ textAlign: 'center', flexShrink: 0, marginLeft: '12px' }}>
                              <div style={{ fontFamily: "'Raleway', sans-serif", fontSize: '20px', fontWeight: '700', color: comp >= 70 ? '#27ae60' : comp >= 40 ? '#d4ac0d' : '#e74c3c' }}>{comp}%</div>
                              <div style={{ fontSize: '9px', color: '#999', textTransform: 'uppercase', letterSpacing: '0.8px' }}>Compliance</div>
                            </div>
                          )}
                        </div>
                      </div>
                      {act.trackCompliance && act.isActive && (
                        <div style={{ padding: '12px 16px', background: '#fafafa', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                          <span style={{ fontSize: '11px', fontWeight: '700', color: '#555', textTransform: 'uppercase', letterSpacing: '0.8px' }}>Today:</span>
                          {slots.map(function(label, si) {
                            var done = isCheckedIn(act, si);
                            var missed = isMissedToday(act, si);
                            var locked = done || missed;
                            return (
                              <div key={si} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <button type="button" onClick={function() { if (!locked) handleCheckin(act, si, false); }}
                                  disabled={locked}
                                  style={{ padding: '6px 14px', borderRadius: '20px', border: '1px solid ' + (done ? '#27ae60' : '#ddd'), background: done ? '#eafaf1' : '#fff', color: done ? '#27ae60' : '#555', fontSize: '12px', fontWeight: '700', cursor: locked ? 'default' : 'pointer', opacity: missed ? 0.4 : 1 }}>
                                  {done ? '✓ ' : ''}{label}
                                </button>
                                {!done && (
                                  <button type="button" onClick={function() { if (!locked) handleCheckin(act, si, true); }}
                                    disabled={locked}
                                    style={{ padding: '6px 14px', borderRadius: '20px', border: '1px solid ' + (missed ? '#e74c3c' : '#e0e0e0'), background: missed ? '#fdf0f0' : '#fff8f8', color: missed ? '#e74c3c' : '#c0392b', fontSize: '12px', fontWeight: '700', cursor: locked ? 'default' : 'pointer' }}>
                                    {missed ? '✗ Missed' : 'Missed'}
                                  </button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                }

                return (
                  <div style={s.patientList}>
                    {myPractActivities.length === 0 ? (
                      <div style={s.emptyCard}>
                        <div style={s.emptyTitle}>No activities assigned</div>
                        <div style={s.emptySub}>Admin will assign practitioner activities here.</div>
                      </div>
                    ) : (
                      <div>
                        {active.length > 0 && (
                          <div style={{ marginBottom: '24px' }}>
                            <div style={{ fontSize: '11px', fontWeight: '700', color: '#4a8f82', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '12px' }}>Active Activities ({active.length})</div>
                            {active.map(renderActivity)}
                          </div>
                        )}
                        {inactive.length > 0 && (
                          <div>
                            <div style={{ fontSize: '11px', fontWeight: '700', color: '#999', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '12px' }}>Past / Inactive ({inactive.length})</div>
                            {inactive.map(renderActivity)}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })()}
        </div>
      </div>
    </div>
  );
};

const styles = {
  layout: { minHeight: '100vh', background: '#f5f8f5', fontFamily: "'Open Sans', sans-serif" },
  topbar: { background: 'linear-gradient(135deg, #071e12, #0c2617)', borderBottom: '3px solid #4a8f82' },
  topbarInner: { maxWidth: '1100px', margin: '0 auto', padding: '0 32px', height: '64px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  logo: { height: '40px' },
  topbarRight: { display: 'flex', alignItems: 'center', gap: '20px' },
  userInfo: { textAlign: 'right' },
  userName: { display: 'block', fontFamily: "'Raleway', sans-serif", fontSize: '14px', fontWeight: '700', color: '#fff' },
  userRole: { fontSize: '10px', color: '#8DC63F', textTransform: 'uppercase', letterSpacing: '1.5px', fontWeight: '700' },
  logoutBtn: { padding: '8px 20px', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '4px', fontFamily: "'Raleway', sans-serif", fontSize: '11px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', color: 'rgba(255,255,255,0.7)', cursor: 'pointer' },
  modal: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' },
  modalCard: { background: '#fff', borderRadius: '10px', padding: '36px', width: '100%', maxWidth: '560px' },
  modalTitle: { fontFamily: "'Raleway', sans-serif", fontSize: '20px', fontWeight: '700', color: '#1a1a2e', marginBottom: '16px' },
  modalPatientInfo: { background: '#f9faf7', borderRadius: '6px', padding: '14px 16px', marginBottom: '24px' },
  modalPatientName: { fontFamily: "'Raleway', sans-serif", fontSize: '15px', fontWeight: '700', color: '#1a1a2e', marginBottom: '4px' },
  modalPatientDetail: { fontSize: '12px', color: '#666' },
  modalBtns: { display: 'flex', gap: '12px', marginTop: '8px' },
  main: { padding: '32px 0' },
  container: { maxWidth: '1100px', margin: '0 auto', padding: '0 32px' },
  tabs: { display: 'flex', gap: '4px', background: '#fff', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '6px', marginBottom: '24px', width: 'fit-content' },
  tab: { padding: '10px 24px', background: 'transparent', border: 'none', borderRadius: '6px', fontFamily: "'Raleway', sans-serif", fontSize: '13px', fontWeight: '700', color: '#666', cursor: 'pointer' },
  tabActive: { background: '#4a8f82', color: '#fff' },
  loading: { textAlign: 'center', padding: '60px', color: '#666', fontSize: '14px' },
  welcomeBanner: { background: 'linear-gradient(135deg, #071e12, #0c2617)', borderRadius: '8px', padding: '28px 32px', marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '24px' },
  welcomeLeft: { flex: 1 },
  welcomeGreeting: { fontFamily: "'Raleway', sans-serif", fontSize: '22px', fontWeight: '700', color: '#fff', marginBottom: '8px' },
  welcomeText: { fontSize: '13px', color: 'rgba(255,255,255,0.55)', lineHeight: '1.7', maxWidth: '500px', marginBottom: '8px' },
  welcomeDate: { fontSize: '12px', color: 'rgba(255,255,255,0.35)' },
  welcomeStats: { display: 'flex', alignItems: 'center', flexShrink: 0, background: 'rgba(255,255,255,0.06)', borderRadius: '8px', padding: '16px 24px' },
  welcomeStat: { textAlign: 'center', padding: '0 20px' },
  welcomeStatNum: { fontFamily: "'Raleway', sans-serif", fontSize: '28px', fontWeight: '700', color: '#8DC63F', lineHeight: 1 },
  welcomeStatLabel: { fontSize: '11px', color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '1px', marginTop: '4px' },
  welcomeStatDivider: { width: '1px', height: '40px', background: 'rgba(255,255,255,0.1)' },
  homeGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px' },
  card: { background: '#fff', border: '1px solid #e0e0e0', borderRadius: '8px', overflow: 'hidden' },
  cardHeader: { padding: '18px 24px', borderBottom: '1px solid #e0e0e0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  cardTitle: { fontFamily: "'Raleway', sans-serif", fontSize: '15px', fontWeight: '700', color: '#1a1a2e' },
  cardSub: { fontSize: '12px', color: '#999', marginTop: '2px' },
  cardBody: { padding: '8px 0' },
  cardLink: { background: 'none', border: 'none', color: '#4a8f82', fontSize: '12px', fontWeight: '700', fontFamily: "'Raleway', sans-serif", cursor: 'pointer', textTransform: 'uppercase' },
  empty: { padding: '32px', textAlign: 'center' },
  emptyCard: { background: '#fff', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '60px', textAlign: 'center' },
  emptyTitle: { fontFamily: "'Raleway', sans-serif", fontSize: '15px', fontWeight: '700', color: '#1a1a2e', marginBottom: '8px' },
  emptySub: { fontSize: '13px', color: '#999' },
  patientItem: { display: 'grid', gridTemplateColumns: '40px 1fr auto auto', gap: '12px', alignItems: 'center', padding: '14px 24px', borderBottom: '1px solid #f5f5f5' },
  patientAvatar: { width: '40px', height: '40px', borderRadius: '50%', background: 'linear-gradient(135deg, #4a8f82, #5a9a6e)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Raleway', sans-serif", fontSize: '13px', fontWeight: '700', color: '#fff', flexShrink: 0 },
  patientInfo: {},
  patientName: { fontFamily: "'Raleway', sans-serif", fontSize: '13px', fontWeight: '700', color: '#1a1a2e', marginBottom: '2px' },
  patientContact: { fontSize: '11px', color: '#666', marginBottom: '2px' },
  patientCondition: { fontSize: '11px', color: '#4a8f82', fontWeight: '600' },
  patientMid: { textAlign: 'center' },
  sessionCount: { fontFamily: "'Raleway', sans-serif", fontSize: '20px', fontWeight: '700', color: '#4a8f82', lineHeight: 1 },
  sessionCountLabel: { fontSize: '10px', color: '#999', textTransform: 'uppercase', letterSpacing: '1px' },
  patientActions: { display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'flex-end' },
  addSessionBtn: { background: '#4a8f82', color: '#fff', border: 'none', borderRadius: '4px', padding: '7px 14px', fontFamily: "'Raleway', sans-serif", fontSize: '11px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', cursor: 'pointer', whiteSpace: 'nowrap' },
  returnBtn: { background: 'transparent', color: '#e74c3c', border: '1px solid #e74c3c', borderRadius: '4px', padding: '7px 14px', fontFamily: "'Raleway', sans-serif", fontSize: '11px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', cursor: 'pointer', whiteSpace: 'nowrap' },
  expandBtn: { background: 'transparent', border: '1px solid #e0e0e0', color: '#666', borderRadius: '4px', padding: '7px 14px', fontFamily: "'Raleway', sans-serif", fontSize: '11px', fontWeight: '700', cursor: 'pointer', whiteSpace: 'nowrap' },
  quickAction: { display: 'flex', alignItems: 'flex-start', gap: '12px', width: '100%', background: 'none', border: 'none', padding: '12px', borderRadius: '6px', cursor: 'pointer', textAlign: 'left', marginBottom: '4px', borderBottom: '1px solid #f5f5f5' },
  quickActionDot: { width: '10px', height: '10px', borderRadius: '50%', flexShrink: 0, marginTop: '4px' },
  quickActionLabel: { fontFamily: "'Raleway', sans-serif", fontSize: '13px', fontWeight: '700', color: '#1a1a2e', marginBottom: '2px' },
  quickActionDesc: { fontSize: '11px', color: '#999' },
  patientList: { display: 'flex', flexDirection: 'column', gap: '16px' },
  patientCard: { background: '#fff', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '24px' },
  patientCardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' },
  patientCardLeft: { display: 'flex', gap: '16px', alignItems: 'flex-start' },
  patientAvatarLg: { width: '48px', height: '48px', borderRadius: '50%', background: 'linear-gradient(135deg, #4a8f82, #5a9a6e)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Raleway', sans-serif", fontSize: '16px', fontWeight: '700', color: '#fff', flexShrink: 0 },
  patientCardName: { fontFamily: "'Raleway', sans-serif", fontSize: '16px', fontWeight: '700', color: '#1a1a2e', marginBottom: '4px' },
  patientCardContact: { fontSize: '12px', color: '#666', marginBottom: '4px' },
  patientCardCondition: { fontSize: '13px', color: '#4a8f82', fontWeight: '600' },
  patientCardRight: { textAlign: 'right' },
  patientCardStats: { marginBottom: '8px' },
  patientCardStat: { textAlign: 'center' },
  patientCardStatNum: { fontFamily: "'Raleway', sans-serif", fontSize: '24px', fontWeight: '700', color: '#4a8f82', lineHeight: 1 },
  patientCardStatLabel: { fontSize: '10px', color: '#999', textTransform: 'uppercase', letterSpacing: '1px' },
  patientCardBadges: { display: 'flex', gap: '6px', justifyContent: 'flex-end' },
  patientCardDesc: { fontSize: '13px', color: '#666', lineHeight: '1.7', padding: '12px 16px', background: '#f9faf7', borderRadius: '6px', marginBottom: '16px' },
  patientCardActions: { display: 'flex', gap: '10px' },
  sessionHistory: { marginTop: '16px', border: '1px solid #e0e0e0', borderRadius: '6px', overflow: 'hidden' },
  sessionHistoryTitle: { fontFamily: "'Raleway', sans-serif", fontSize: '11px', fontWeight: '700', color: '#666', textTransform: 'uppercase', letterSpacing: '1px', padding: '12px 16px', background: '#f9faf7', borderBottom: '1px solid #e0e0e0' },
  sessionHistoryItem: { display: 'grid', gridTemplateColumns: '140px 1fr auto', gap: '16px', alignItems: 'start', padding: '14px 16px', borderBottom: '1px solid #f5f5f5' },
  sessionHistoryLeft: { display: 'flex', gap: '10px', alignItems: 'flex-start' },
  sessionNum: { width: '24px', height: '24px', borderRadius: '50%', background: '#4a8f82', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: '700', flexShrink: 0 },
  sessionDate: { fontFamily: "'Raleway', sans-serif", fontSize: '13px', fontWeight: '700', color: '#1a1a2e', marginBottom: '3px' },
  sessionDuration: { fontSize: '11px', color: '#999' },
  sessionRemarks: { fontSize: '13px', color: '#555', lineHeight: '1.6' },
  badge: { fontFamily: "'Raleway', sans-serif", fontSize: '10px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', padding: '4px 10px', borderRadius: '20px', whiteSpace: 'nowrap' },
  formGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' },
  field: { marginBottom: '16px' },
  label: { display: 'block', fontSize: '10px', fontWeight: '700', letterSpacing: '1.5px', textTransform: 'uppercase', color: '#666', marginBottom: '6px', fontFamily: "'Raleway', sans-serif" },
  input: { width: '100%', padding: '10px 14px', border: '1px solid #e0e0e0', borderRadius: '5px', fontSize: '14px', fontFamily: "'Open Sans', sans-serif", boxSizing: 'border-box', color: '#333' },
  submitBtn: { background: '#4a8f82', color: '#fff', border: 'none', borderRadius: '4px', padding: '12px 28px', fontFamily: "'Raleway', sans-serif", fontSize: '12px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', cursor: 'pointer' },
  cancelBtn: { background: 'transparent', color: '#666', border: '1px solid #e0e0e0', borderRadius: '4px', padding: '12px 24px', fontFamily: "'Raleway', sans-serif", fontSize: '12px', fontWeight: '700', cursor: 'pointer' }
};
 
export default HealerDashboard;